import React, { useState, useEffect, useRef } from 'react';
import Header from './components/Header';
import Footer from './components/Footer';
import TapGame from './components/TapGame';
import Login from './components/Login';
import TasksPage from './components/TasksPage';
import FriendsPage from './components/FriendsPage';
import Shop from './components/Shop';
import Fight from './components/Fight';
import Leaderboard from './components/Leaderboard';
import SplashScreen from './components/SplashScreen';
import DailyCheckInModal from './components/DailyCheckInModal';
import Battle from './components/Battle';
import Marketplace from './components/Marketplace';
import { motion } from 'framer-motion';
import './index.css';
import SafeAreaBinder from './SafeAreaBinder';
import GamepassPage from './components/GamepassPage';
import { apiFetch, apiJson } from './components/apiFetch';
import YearInReviewPage from './components/YearInReviewPage';
import HolidayEvent from './components/HolidayEvent';
import ThematicEvent from './components/ThematicEvent';
import SettingsModal from './components/SettingsModal';
import HumanCheckpoint from './components/HumanCheckpoint';
import Arenas from "./components/Arenas";
import ArenaLeaderboard from './components/ArenaLeaderboard';
import FortuneWheel from "./components/FortuneWheel";
import HiddenSeek from "./components/HiddenSeek";
import BattleStreakEvent from "./components/BattleStreakEvent";
import SeasonChangeModal from "./components/SeasonChangeModal";
import TournamentModal from "./components/TournamentModal";
import Tournaments from "./components/Tournaments";
import TournamentsV2 from "./components/TournamentsV2";
import MyTapisPage from './components/MyTapisPage';
import Blocker from './components/Blocker';
import DefenseDeck from "./components/DefenseDeck";
import MemoryGame from "./components/MemoryGame";
import Blog from './components/Blog';
import InventoryHub from './components/InventoryHub';
import CurrencyPurchaseModal from "./components/CurrencyPurchaseModal";
import ProfileModal from "./components/ProfileModal";
import EventsPage from './components/EventsPage';
import GamesHub from "./components/GamesHub";
import BattlePassPage from './components/BattlePassPage';
import ChallengePage from "./components/ChallengePage";
import Guilds from "./components/Guilds";
import GuildBrowser from "./components/GuildBrowser";
import AddHomeScreen from "./components/AddHomeScreen";
import { useAppConfig } from './ConfigContext';

const parseMs = (iso) => {
  const t = Date.parse(String(iso || ''));
  return Number.isFinite(t) ? t : null;
};

const isNowBetween = (nowMs, startIso, endIso) => {
  const s = parseMs(startIso);
  const e = parseMs(endIso);
  if (s == null || e == null) return false;
  return nowMs >= s && nowMs <= e;
};

function getDeviceId() {
  try {
    let id = localStorage.getItem('device_id');

    if (!id) {
      id =
        globalThis.crypto?.randomUUID?.() ||
        `dev_${Date.now()}_${Math.random().toString(36).slice(2)}`;

      try {
        localStorage.setItem('device_id', id);
      } catch {}
    }

    return id;
  } catch {
    return `dev_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  }
}

function isImageIcon(value) {
  const s = String(value || '').trim().toLowerCase();

  return (
    s.startsWith('http://') ||
    s.startsWith('https://') ||
    s.startsWith('/') ||
    s.startsWith('./') ||
    s.startsWith('../') ||
    s.endsWith('.png') ||
    s.endsWith('.jpg') ||
    s.endsWith('.jpeg') ||
    s.endsWith('.webp') ||
    s.endsWith('.gif') ||
    s.endsWith('.svg')
  );
}

function renderRichText(input) {
  const text = String(input ?? '');

  const STYLES = {
    gold:   { color: '#ffd700', fontWeight: 800 },
    red:    { color: '#ff6b6b', fontWeight: 800 },
    green:  { color: '#7CFC00', fontWeight: 800 },
    orange: { color: '#FFAF56', fontWeight: 800 },
    dim:    { opacity: 0.85 },
    center: { display: 'block', textAlign: 'center' },
  };

  const ALLOWED = new Set(Object.keys(STYLES));

  // Recursively parses nested tags.
  const renderNodes = (src, keyPrefix = 'rt') => {
    const s = String(src ?? '');
    const nodes = [];

    let i = 0;
    let key = 0;

    while (i < s.length) {
      const open = s.indexOf('[', i);
      if (open === -1) {
        nodes.push(s.slice(i));
        break;
      }

      // push text before tag
      if (open > i) nodes.push(s.slice(i, open));

      const close = s.indexOf(']', open + 1);
      if (close === -1) {
        nodes.push(s.slice(open));
        break;
      }

      const tagNameRaw = s.slice(open + 1, close).trim();
      const tagName = tagNameRaw.toLowerCase();

      // not an allowed tag -> treat as text
      if (!ALLOWED.has(tagName)) {
        nodes.push(s.slice(open, close + 1));
        i = close + 1;
        continue;
      }

      const endTag = `[/${tagNameRaw}]`; // keep exact case? safer to search case-insensitive below
      // find matching closing tag (case-insensitive)
      const rest = s.slice(close + 1);
      const endIdxLower = rest.toLowerCase().indexOf(`[/${tagName}]`);
      if (endIdxLower === -1) {
        // no closing tag -> treat as text
        nodes.push(s.slice(open, close + 1));
        i = close + 1;
        continue;
      }

      const inner = rest.slice(0, endIdxLower);
      const after = close + 1 + endIdxLower + (`[/${tagName}]`).length;

      nodes.push(
        <span key={`${keyPrefix}-${key++}`} style={STYLES[tagName]}>
          {renderNodes(inner, `${keyPrefix}-${key}-inner`)}
        </span>
      );

      i = after;
    }

    return nodes;
  };

  return <>{renderNodes(text)}</>;
}

const WELCOME_OFFER_END_AT = "2026-12-16T23:59:00-03:00";

function formatCountdown(ms) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));

  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${String(days).padStart(2, "0")}D ${String(hours).padStart(2, "0")}H ${String(minutes).padStart(2, "0")}M ${String(seconds).padStart(2, "0")}S`;
}

function OfferCountdown({ targetIso }) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => clearInterval(id);
  }, []);

  const targetMs = Date.parse(targetIso);
  const remainingMs = Number.isFinite(targetMs)
    ? Math.max(0, targetMs - now)
    : 0;

  return <>{formatCountdown(remainingMs)}</>;
}

function App() {
const shouldOpenAllSeasonModalRef = useRef(false);
const [showSeasonChangeModal, setShowSeasonChangeModal] = useState(false);
const [seasonChangeMode, setSeasonChangeMode] = useState("newUser");
const giftModalShownRef = useRef(false);
const promoRedeemInFlightRef = useRef(false);
const { loaded: cfgLoaded, tournamentsActive, tournaments, fortuneWheel } = useAppConfig();
const [showSeasonModal, setShowSeasonModal] = useState(false);
const [showTournamentsModal, setShowTournamentsModal] = useState(false);
const nowMs = Date.now();

//const BLOCKER_TARGET_DATE = '2026-08-20T12:00:00Z';
const BLOCKER_TARGET_DATE = '';
//const BLOCKER_BYPASS_TELEGRAM_IDS = [1504545975];
const BLOCKER_BYPASS_TELEGRAM_IDS = [];

const [currencyModalOpen, setCurrencyModalOpen] = useState(false);
const [currencyModalType, setCurrencyModalType] = useState("sparks");
const [profileOpen, setProfileOpen] = useState(false);

const openCurrencyPurchaseModal = (which) => {
  setCurrencyModalType(which);
  setCurrencyModalOpen(true);
};

const blockerEnabled = typeof BLOCKER_TARGET_DATE === 'string' && BLOCKER_TARGET_DATE.trim() !== '';

const [showTwiceDailyModal, setShowTwiceDailyModal] = useState(false);
const [twiceDailyModalData, setTwiceDailyModalData] = useState(null);

const [showHighTrophiesModal, setShowHighTrophiesModal] = useState(false);
const [showTournamentTwiceDailyModal, setShowTournamentTwiceDailyModal] = useState(false);
const tournamentModalOpenedThisVisitRef = useRef(false);

const allTournaments = React.useMemo(
  () => (Array.isArray(tournaments?.tournaments) ? tournaments.tournaments : []),
  [tournaments]
);

const activeTournament = React.useMemo(() => {
  return (
    allTournaments.find((t) => isNowBetween(nowMs, t?.startAt, t?.endAt)) ?? null
  );
}, [allTournaments, nowMs]);

const displayTournament = React.useMemo(() => {
  if (activeTournament) return activeTournament;

  const lastEnded = allTournaments
    .map((t) => ({
      ...t,
      endMs: parseMs(t?.endAt),
    }))
    .filter((t) => t.endMs != null && t.endMs <= nowMs)
    .sort((a, b) => b.endMs - a.endMs)[0];

  return lastEnded ?? allTournaments[0] ?? null;
}, [activeTournament, allTournaments, nowMs]);

const TOURNAMENT_MODAL_ENABLED = cfgLoaded && !!activeTournament;
const ACTIVE_TOURNAMENT_ID = activeTournament?.id ?? displayTournament?.id ?? null;
const TOURNAMENT_START_DATE = displayTournament?.startAt ?? null;
const TOURNAMENT_END_DATE = displayTournament?.endAt ?? null;
const TOURNAMENT_TYPE = displayTournament?.side ?? null;

const tournamentBadgeOn = cfgLoaded ? Boolean(tournaments?.showBadge) : false;
const hasTournament = TOURNAMENT_MODAL_ENABLED || tournamentBadgeOn;

const isNewUserRef = useRef(false);

const [user, setUser] = useState(null);
const [page, setPage] = useState('arenas');

const [hasBattlePass, setHasBattlePass] = useState(false);
const [battlePassLoading, setBattlePassLoading] = useState(false);

const [pageParams, setPageParams] = useState({});
const [isLoading, setIsLoading] = useState(true);

const [showSettings, setShowSettings] = useState(false);

const [showWarning, setShowWarning] = useState(false)
const [showBlocked, setShowBlocked] = useState(false)

const [copied, setCopied] = useState(false);

const [unbanCode, setUnbanCode] = useState('');
const [unbanError, setUnbanError] = useState('');
const [unbanBusy, setUnbanBusy] = useState(false);
const [unbanImgTick, setUnbanImgTick] = useState(Date.now());

const [cmsModalsCfg, setCmsModalsCfg] = useState([]);
const [showCmsModal, setShowCmsModal] = useState(false);

const HIGH_TROPHIES_MODAL_ENABLED = true;
const HIGH_TROPHIES_MIN = 12001;

useEffect(() => {
  if (isLoading) return;
  if (!HIGH_TROPHIES_MODAL_ENABLED) return;
  if (!user?.id) return;

  if (Number(user?.trophies || 0) < HIGH_TROPHIES_MIN) return;

  try {
    const now = new Date();

    const yyyy = now.getUTCFullYear();
    const mm = String(now.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(now.getUTCDate()).padStart(2, '0');

    const utcDay = `${yyyy}-${mm}-${dd}`;

    const storageKey =
      `highTrophiesModalShown3:${user.id}:${utcDay}`;

    if (localStorage.getItem(storageKey) === '1') return;

    localStorage.setItem(storageKey, '1');

    setShowHighTrophiesModal(true);
  } catch (e) {
    console.warn('High trophies modal daily check failed:', e);

    // If localStorage fails, still show the modal
    setShowHighTrophiesModal(true);
  }
}, [
  isLoading,
  user?.id,
  user?.trophies
]);

const [cmsModal, setCmsModal] = useState(null);

  const bgmRef = useRef(null);
  const [audioUnlocked, setAudioUnlocked] = useState(false);

  const SOUND_KEY = 'sound_enabled';
  const HOME_BGM_SRC = '/bg_music.mp3';
  const BATTLE_BGM_SRC = '/battle_music.mp3';

const [soundEnabled, setSoundEnabled] = useState(() => {
  try {
    const v = localStorage.getItem(SOUND_KEY);
    return v == null ? false : v === '1';
  } catch {
    return false;
  }
});

const tournamentKey =
  TOURNAMENT_START_DATE && TOURNAMENT_END_DATE
    ? `tournamentModal:${TOURNAMENT_START_DATE}-${TOURNAMENT_END_DATE}`
    : null;

useEffect(() => {
  if (isLoading) return;
  if (!TOURNAMENT_MODAL_ENABLED) return;
  if (!tournamentKey) return;

  const startParam = String(
    window?.Telegram?.WebApp?.initDataUnsafe?.start_param || ''
  ).trim().toLowerCase();

  // Profile deeplink has priority
  if (startParam === 'profile') return;

  if (!localStorage.getItem(tournamentKey)) {
    setShowTournamentsModal(true);
  }
}, [isLoading, TOURNAMENT_MODAL_ENABLED, tournamentKey]);

const preloadImages = (urls = []) => {
  urls.forEach((src) => {
    const img = new Image();
    img.src = src;
  });
};

useEffect(() => {
  preloadImages([
    // GLOBAL
    "/close.png",
    "/spider-popup.png",
    "/giveaway.png",
    "/passbanner1.png",
    "/passbanner2.png",
    "/sound_on.png",
    "/sound_off.png",
    "/settings_1.png",
    "/settings_2.png",
    "/settings_3.png",

    // ARENAS - shortcuts
    "/tapicon.png?v1",
    "/event_icon.png?v1",
    "/icon_rune.png?v1",
    "/icodrop.png",
    "/fortunebt.png?v1",
    "/memory_big.png?v1",

    // ARENAS - chests
    "/shop_chest_1.png",
    "/shop_chest_2.png",
    "/shop_chest_3.png",
    "/shop_chest_5.png",

    // ARENAS - UI
    "/v2/trophy.png",
    "/v2/nocard.png",
    "/v2/switch.png",
    "/v2/openchest.png",
    "/v2/clock.png",
    "/v2/check.png",
    "/v2/lock.png",
    "/sparks.png",
    "/runes.png",
  ]);
}, []);

const MUSIC_VOL_KEY = 'music_volume';
const SFX_VOL_KEY = 'sfx_volume';
const SFX_ENABLED_KEY = 'sfx_enabled';

const clamp01 = (n) => Math.max(0, Math.min(1, Number(n) || 0));

const [musicVolume, setMusicVolume] = useState(() => {
  const v = localStorage.getItem(MUSIC_VOL_KEY);
  return v == null ? 0.25 : clamp01(v);
});

const [sfxVolume, setSfxVolume] = useState(() => {
  const v = localStorage.getItem(SFX_VOL_KEY);
  return v == null ? 1.00 : clamp01(v);
});


const [sfxEnabled, setSfxEnabled] = useState(() => {
  const v = localStorage.getItem(SFX_ENABLED_KEY);
  return v == null ? true : v === '1';
});

useEffect(() => { try { localStorage.setItem(MUSIC_VOL_KEY, String(musicVolume)); } catch {} }, [musicVolume]);
useEffect(() => { try { localStorage.setItem(SFX_VOL_KEY, String(sfxVolume)); } catch {} }, [sfxVolume]);
useEffect(() => { try { localStorage.setItem(SFX_ENABLED_KEY, sfxEnabled ? '1' : '0'); } catch {} }, [sfxEnabled]);

const appBootKeyRef = useRef(
  `${Date.now()}_${Math.random().toString(36).slice(2)}`
);
const cmsDismissedThisBootRef = useRef(new Set());

useEffect(() => {
  const a = bgmRef.current;
  if (!a) return;
  a.volume = Math.max(0, Math.min(1, Number(musicVolume) || 0));
  a.muted = !soundEnabled; // your existing master toggle
}, [musicVolume, soundEnabled]);

useEffect(() => {
  const onVis = () => {
    const a = bgmRef.current;
    if (!a) return;

    if (document.hidden) {
      try { a.pause(); } catch {}
    } else {
      // resume if allowed
      if (soundEnabled && audioUnlocked) {
        a.play().catch(() => {});
      }
    }
  };

  document.addEventListener('visibilitychange', onVis);
  return () => document.removeEventListener('visibilitychange', onVis);
}, [soundEnabled, audioUnlocked]);

const toggleSound = () => {
  setSoundEnabled(prev => {
    const next = !prev;
    try { localStorage.setItem(SOUND_KEY, next ? '1' : '0'); } catch {}
    return next;
  });
};

const isCampaignStart = (s) => {
  const v = String(s || '').trim().toLowerCase();
  return v.startsWith('cmp_') || v.startsWith('cmp:');
};

const parseCampaignCode = (s) => {
  const v = String(s || '').trim();
  if (!v) return null;
  if (v.toLowerCase().startsWith('cmp:')) return v.slice(4).trim();
  if (v.toLowerCase().startsWith('cmp_')) return v.slice(4).trim();
  return null;
};

useEffect(() => {
  return;
  const tg = window.Telegram?.WebApp;
  if (!tg) return;



  // Only run when inside Telegram AND the app was NOT opened from a bot button
  const launchedFromLink =
    !tg.initDataUnsafe?.start_param &&
    !tg.initData &&
    !tg.initDataUnsafe?.user;

  if (launchedFromLink) {
    const bot = process.env.REACT_APP_TELEGRAM_BOT_USERNAME;

    // Redirect user so the WebApp reopens in correct context
    tg.openTelegramLink(`https://t.me/${bot}?startapp=fix`);
  }
}, []);
  
  
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showDailyModal, setShowDailyModal] = useState(false);
  const [dailyData, setDailyData] = useState(null);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [walletInput, setWalletInput] = useState('');
  const [walletSaving, setWalletSaving] = useState(false);
  const [walletError, setWalletError] = useState('');
  const [navSource, setNavSource] = useState('Footer');
  const [shopView, setShopView] = useState('hub');
  
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [gift, setGift] = useState(null);

  const pageRef = useRef(page);
const musicVolumeRef = useRef(musicVolume);
const audioUnlockedRef = useRef(audioUnlocked);

useEffect(() => { pageRef.current = page; }, [page]);
useEffect(() => { musicVolumeRef.current = musicVolume; }, [musicVolume]);
useEffect(() => { audioUnlockedRef.current = audioUnlocked; }, [audioUnlocked]);

useEffect(() => {
  if (!soundEnabled) {
    const a = bgmRef.current;
    if (a) {
      try { a.pause(); a.currentTime = 0; } catch {}
    }
    return;
  }

  if (bgmRef.current) return;

  const initialSrc = (pageRef.current === 'battle') ? BATTLE_BGM_SRC : HOME_BGM_SRC;
  const a = new Audio(initialSrc);
  a.loop = true;
  a.preload = 'none'; // good for older devices
  a.volume = Math.max(0, Math.min(1, Number(musicVolumeRef.current) || 0));
  a.setAttribute('data-src', initialSrc);
  bgmRef.current = a;

  if (audioUnlockedRef.current) a.play().catch(() => {});

  return () => {
    try { a.pause(); a.currentTime = 0; } catch {}
    if (bgmRef.current === a) bgmRef.current = null;
  };
}, [soundEnabled]);

// 2) volume/mute
useEffect(() => {
  const a = bgmRef.current;
  if (!a) return;
  a.volume = Math.max(0, Math.min(1, Number(musicVolume) || 0));
  a.muted = !soundEnabled;
}, [musicVolume, soundEnabled]);

// 3) quando destravar áudio, tenta tocar
useEffect(() => {
  const a = bgmRef.current;
  if (!a) return;
  if (!soundEnabled || !audioUnlocked) return;
  a.play().catch(() => {});
}, [audioUnlocked, soundEnabled]);

// 4) troca faixa quando muda de página (sem recriar)
useEffect(() => {
  const a = bgmRef.current;
  if (!a) return;

  const wantSrc = (page === 'battle') ? BATTLE_BGM_SRC : HOME_BGM_SRC;
  const currentSrc = a.getAttribute('data-src') || '';
  if (currentSrc === wantSrc) return;

  const wasPlaying = !a.paused;

  a.setAttribute('data-src', wantSrc);
  try { a.pause(); a.currentTime = 0; } catch {}
  a.src = wantSrc;

  if (soundEnabled && audioUnlocked && wasPlaying) {
    a.play().catch(() => {});
  }
}, [page, soundEnabled, audioUnlocked]);

// 5) pause/resume ao esconder/voltar
useEffect(() => {
  const onVis = () => {
    const a = bgmRef.current;
    if (!a) return;

    if (document.hidden) {
      try { a.pause(); } catch {}
    } else if (soundEnabled && audioUnlocked) {
      a.play().catch(() => {});
    }
  };

  document.addEventListener('visibilitychange', onVis);
  return () => document.removeEventListener('visibilitychange', onVis);
}, [soundEnabled, audioUnlocked]);


  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (!tg) return;

    tg.ready();
    tg.expand();
    tg.disableVerticalSwipes?.();
    //alert(`tg? ${!!tg}\n` + `version: ${tg?.version ?? '(missing)'}\n` + `platform: ${tg?.platform ?? '(missing)'}\n` +  `initData length: ${tg?.initData?.length ?? 0}`);
  }, []);

  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');
  const [feedbackSuccess, setFeedbackSuccess] = useState('');

  // Global side selection (must happen before anything else like welcome pack)
  const [showGlobalSideModal, setShowGlobalSideModal] = useState(false);
  const [isChoosingSide, setIsChoosingSide] = useState(false);
  const [sideErrorMessage, setSideErrorMessage] = useState('');
  const [selectedSideIndex, setSelectedSideIndex] = useState(0);

  // Whitelist flag + modal
  const [whitelistEnabled, setWhitelistEnabled] = useState(false);
  const [showWhitelistModal, setShowWhitelistModal] = useState(false);
  const [wlBlockedAction, setWlBlockedAction] = useState(''); // e.g., 'leaderboard', 'shop', 'fight'

  const [newUsersLocked, setNewUsersLocked] = useState(false);
  const [showEarlyLockModal, setShowEarlyLockModal] = useState(false);
  const [lockBlockedAction, setLockBlockedAction] = useState('');
  const isEarlyBirdLocked = () => newUsersLocked && user?.early_bird === false;

  const [wlTargetIso, setWlTargetIso] = useState(null);          // e.g. from /api/config
  const [wlInitialRemainingMs, setWlInitialRemainingMs] = useState(null);
  const [wlPerfStart, setWlPerfStart] = useState(null);
  const [wlTick, setWlTick] = useState(0);

  const formatDhms = (ms) => {
    const total = Math.max(0, Math.floor(ms / 1000));
    const days = Math.floor(total / 86400);
    const hours = Math.floor((total % 86400) / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    return `${String(days).padStart(2,'0')}d ${String(hours).padStart(2,'0')}h ${String(minutes).padStart(2,'0')}m ${String(seconds).padStart(2,'0')}s`;
  };

  // Read server "now" from response (mirrors TapGame pattern)
  const getServerNowMs = (res, data) => {
    const tryParse = (v) => {
      const t = Date.parse(v || '');
      return Number.isFinite(t) ? t : null;
    };
    if (data?.serverNow) {
      const t = tryParse(data.serverNow);
      if (t != null) return t;
    }
    const hdrDate = res?.headers?.get?.('Date');
    return tryParse(hdrDate); // may be null if header missing
  };

  const startWhitelistCountdown = (serverNowMs, targetIso) => {
    if (!Number.isFinite(serverNowMs)) return;
    const targetMs = Date.parse(targetIso);
    if (!Number.isFinite(targetMs)) return;
    const remaining = Math.max(0, targetMs - serverNowMs);
    setWlInitialRemainingMs(remaining);
    setWlPerfStart(performance.now());
  };

  const isGated = () => whitelistEnabled || isEarlyBirdLocked();
  const showGateModal = (action = '') => {
    if (isEarlyBirdLocked()) {
      setLockBlockedAction(action);
      setShowEarlyLockModal(true);
    } else {
      setWlBlockedAction(action);
      setShowWhitelistModal(true);
    }
  };
  const gate = (action, allowedFn) => {
    if (isGated()) { showGateModal(action); return; }
    if (typeof allowedFn === 'function') allowedFn();
  };



  // --- Mobile-only guard (STRICT) -------------------------------------------
  const EMAIL_LOGIN_ENABLED =
    (process.env.REACT_APP_REQUIRE_TMA ?? process.env.REACT_APP_ENABLE_TMA_DEV) !== '1';

  const MOBILE_MODE = (process.env.REACT_APP_MOBILE_ONLY || 'prod').toLowerCase();
  const mobileGuardEnabled =
    MOBILE_MODE === 'always' ||
    (MOBILE_MODE === 'prod' && process.env.NODE_ENV === 'production');

  const isStrictMobile = () => {
    if (typeof navigator === 'undefined') return true;
    const ua = navigator.userAgent || '';
    const uaDataMobile = navigator.userAgentData?.mobile === true;
    const isAndroid = /Android/i.test(ua);
    const isIPhone = /iPhone|iPod/i.test(ua);
    const isIPad = /iPad/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
    const isTelegramDesktop = /TelegramDesktop/i.test(ua);
    const isDesktopOS = /Windows|Macintosh|Linux/i.test(ua) && !isIPad;
    const isDesktopChromeEmu = isDesktopOS && /Chrome/i.test(ua) && !/Mobile/i.test(ua);
    const isMobile = uaDataMobile || isAndroid || isIPhone || isIPad;
    return isMobile && !isTelegramDesktop && !isDesktopChromeEmu;
  };

  const onNavigate = (next, params = {}) => { setPageParams(params); setPage(next); };
  const [isMobile, setIsMobile] = useState(isStrictMobile());
  useEffect(() => {
    const onResize = () => setIsMobile(isStrictMobile());
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);
  // --------------------------------------------------------------------------


  useEffect(() => {
  const tg = window.Telegram?.WebApp;
  const root = document.documentElement;

  const getBottomSafeArea = () => {
    const safeBottom = Number(tg?.safeAreaInset?.bottom || 0);
    const contentBottom = Number(tg?.contentSafeAreaInset?.bottom || 0);

    return Math.max(safeBottom, contentBottom, 0);
  };

  const applyBottomSafeArea = () => {
    const rawBottom = getBottomSafeArea();
      
     const bottom = rawBottom < 20
    ? 0
    : Math.max(0, rawBottom - 8);

    root.style.setProperty('--footer-bottom-offset', `${bottom}px`);
    //root.style.setProperty('--footer-bottom-offset', `50px`);

    console.log('[SAFE AREA]', {
      safeAreaInset: tg?.safeAreaInset,
      contentSafeAreaInset: tg?.contentSafeAreaInset,
      bottom,
    });
  };

  applyBottomSafeArea();

  tg?.onEvent?.('safeAreaChanged', applyBottomSafeArea);
  tg?.onEvent?.('contentSafeAreaChanged', applyBottomSafeArea);
  tg?.onEvent?.('viewportChanged', applyBottomSafeArea);

  window.addEventListener('resize', applyBottomSafeArea);
  window.addEventListener('orientationchange', applyBottomSafeArea);

  return () => {
    tg?.offEvent?.('safeAreaChanged', applyBottomSafeArea);
    tg?.offEvent?.('contentSafeAreaChanged', applyBottomSafeArea);
    tg?.offEvent?.('viewportChanged', applyBottomSafeArea);

    window.removeEventListener('resize', applyBottomSafeArea);
    window.removeEventListener('orientationchange', applyBottomSafeArea);
  };
}, []);

  // ----

    const submitFeedback = async () => {
    const msg = feedbackMessage.trim();
    if (msg.length < 3) {
      setFeedbackError('Please write at least 3 characters.');
      return;
    }

    try {
      setFeedbackSending(true);
      setFeedbackError('');
      setFeedbackSuccess('');

      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/feedbacks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          message: msg,
          name: user?.name || null,
          email: user?.email || null,
          userId: user?.id || null,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.error || 'Failed to send feedback.');
      }

      setFeedbackSuccess('Thanks! Your feedback has been sent. 💌');
      setFeedbackMessage('');
      // auto-close after a short delay
      setTimeout(() => {
        setShowFeedbackModal(false);
        setFeedbackSuccess('');
      }, 1200);
    } catch (e) {
      console.error('Feedback submit error:', e);
      setFeedbackError(e?.message || 'Something went wrong. Please try again.');
    } finally {
      setFeedbackSending(false);
    }
  };


  const toggleMenu = () => setIsMenuOpen(prev => !prev);

  const needsHumanCheck = user?.needs_human_check === true;

  // Daily rewards
 const openDailyRewards = async () => {
  if (needsHumanCheck) {
    return {
      ok: false,
      blocked: 'humancheck',
    };
  }

  try {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/daily`,
      {
        headers: {
          Authorization:
            `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );

    const data = await res.json();

    if (!res.ok) {
      console.error(
        'Daily endpoint error:',
        data?.error ||
        res.statusText
      );

      return false;
    }

   setUser(prev => ({
  ...prev,
  sparks: data.sparks ?? prev.sparks,
  runes: data.runes ?? prev.runes,
  tapi: data.tapi ?? prev.tapi,
  tickets: data.tickets ?? prev.tickets,
}));

    setDailyData(data);
    setShowDailyModal(true);

    return true;

  } catch (err) {
    console.error(
      'Failed to open daily rewards:',
      err
    );

    return false;
  }
};

  const navigate = (nextPage, source = null, params = {}) => {
    setNavSource(source);
    onNavigate(nextPage, params);
  };

    useEffect(() => {
    if (!user) return;

    if (user.is_blocked) {
    setShowBlocked(true)
    return
  }

const wc = Number(user.warning_count || 0);

if (wc > 0) {
  const key = `warnModalShown:${user.id}:${wc}`;
  try {
    if (localStorage.getItem(key) !== '1') {
      localStorage.setItem(key, '1');
      setShowWarning(true);
    }
  } catch {
    // if storage fails, still show it
    setShowWarning(true);
  }
}

    const level = Number(user.level);
    if (!Number.isFinite(level) || level < 3) return;

    const userId = user.id || user.user_id || 'anon';
    const storageKey = `feedback_shown_level3_${userId}`;

    if (localStorage.getItem(storageKey) === '1') return;

    localStorage.setItem(storageKey, '1');
    openFeedbackModal();
  }, [user]);


  useEffect(() => {
    if (showWalletModal) {
      setWalletInput(user?.wallet || '');
      setWalletError('');
    }
  }, [showWalletModal, user]);

  
const isTonAddressLike = (addr) => {
  if (typeof addr !== 'string') return false;
  const s = addr.trim();

  // Raw form: workchain:64-hex
  if (/^-?\d+:[0-9a-fA-F]{64}$/.test(s)) {
    return true;
  }

  // User-friendly form: base64url-ish, 48–55 chars
  if (/^[A-Za-z0-9\-_]{48,55}$/.test(s)) {
    return true;
  }

  return false;
};

  const openFeedbackModal = () => {
    setFeedbackMessage('');
    setFeedbackError('');
    setFeedbackSuccess('');
    setShowFeedbackModal(true);
  };


  const openWalletModal = () => {
    setWalletInput(user?.wallet || '');
    setWalletError('');
    setShowWalletModal(true);
  };

  const openBotChat = (payload) => {
    const tg = window?.Telegram?.WebApp;
    const username = process.env.REACT_APP_TELEGRAM_BOT_USERNAME;
    if (!tg || !username) return;
    try { tg.close(); return; } catch (_) {}
    const url = payload
      ? `tg://resolve?domain=${username}&start=${encodeURIComponent(payload)}`
      : `tg://resolve?domain=${username}`;
    tg.openTelegramLink(url);
  };

  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (tg?.disableVerticalSwipes) tg.disableVerticalSwipes();
  }, []);

  const saveWallet = async () => {
    const addr = walletInput.trim();
    if (!isTonAddressLike(addr)) {
      setWalletError('Please enter a valid TON wallet address.');
      return;
    }
    try {
      setWalletSaving(true);
      setWalletError('');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/wallet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ wallet: addr }),
      });
      const data = await res.json();
      if (!res.ok) { setWalletError(data?.error || 'Failed to save wallet.'); return; }
      setUser((prev) => ({ ...prev, wallet: addr }));
      setShowWalletModal(false);
    } catch (e) {
      console.error('Save wallet error:', e);
      setWalletError('Something went wrong. Please try again.');
    } finally {
      setWalletSaving(false);
    }
  };

  const guardedNavigate = (nextPage, source = null, params = {}) => {
    if (nextPage !== 'home' && isGated()) { showGateModal(nextPage); return; }
    setNavSource(source); onNavigate(nextPage, params);
  };

// read global whitelist from admin CMS
useEffect(() => {
  let aborted = false;
  (async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/config`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return;
      const cfg = await res.json();
     if (aborted) return;
      if (typeof cfg?.whitelistEnabled === 'boolean') {
        setWhitelistEnabled(cfg.whitelistEnabled);
      }
      if (typeof cfg?.newUsersLocked === 'boolean') {
        setNewUsersLocked(cfg.newUsersLocked);
      }
      if (typeof cfg?.whitelistTarget === 'string') {
  setWlTargetIso(cfg.whitelistTarget);
}

const modalsArr = Array.isArray(cfg?.modals)
  ? cfg.modals
  : Array.isArray(cfg?.modals?.modals)
    ? cfg.modals.modals
    : Array.isArray(cfg?.modalsConfig?.modals)
      ? cfg.modalsConfig.modals
      : [];

setCmsModalsCfg(modalsArr);


    } catch (e) {
      console.warn('[WL] /api/config fetch failed', e);
    }
  })();
  return () => { aborted = true; };
}, []);


useEffect(() => {
  (async () => {
    if (!wlTargetIso) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/status`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json().catch(() => ({}));
      const serverNowMs = getServerNowMs(res, data);
      // Log for quick diagnosis
      if (serverNowMs != null) startWhitelistCountdown(serverNowMs, wlTargetIso);
    } catch (e) {
    console.warn('[WL] /api/status failed', e);
    }
  })();
}, [wlTargetIso, user?.id]);


// Monotonic 1s ticker for the countdown
useEffect(() => {
  if (wlInitialRemainingMs == null || wlPerfStart == null) return;
  const id = setInterval(() => setWlTick((t) => t + 1), 1000);
  return () => clearInterval(id);
}, [wlInitialRemainingMs, wlPerfStart]);

const wlRemainingMs = (() => {
  if (wlInitialRemainingMs == null || wlPerfStart == null) return null;
  const elapsed = performance.now() - wlPerfStart;
  return Math.max(0, wlInitialRemainingMs - elapsed);
})();


// Load user (auto re-auth on 401 in Telegram, then retry)
useEffect(() => {
  (async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const userData = await apiJson('/api/me');
      setUser(userData);

      if (typeof userData?.whitelistEnabled === 'boolean') {
        setWhitelistEnabled(userData.whitelistEnabled);
      }
      if (typeof userData?.newUsersLocked === 'boolean') {
        setNewUsersLocked(userData.newUsersLocked);
      }


    } catch (err) {
      console.error('User fetch error:', err?.message || err);
      localStorage.removeItem('token');
      setUser(null);
    }
  })();
}, [user?.id]);


useEffect(() => {
  if (!user?.id) {
    setHasBattlePass(false);
    setBattlePassLoading(false);
    return;
  }

  const token = localStorage.getItem("token");

  if (!token) {
    setHasBattlePass(false);
    setBattlePassLoading(false);
    return;
  }

  let cancelled = false;

  async function loadBattlePassStatus() {
    try {
      setBattlePassLoading(true);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/battle-pass`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-TG-INITDATA":
              window.Telegram?.WebApp?.initData || "",
          },
        }
      );

      const data = await response
        .json()
        .catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.error ||
          `Battle Pass request failed: HTTP ${response.status}`
        );
      }

      if (cancelled) return;

      const source =
        data?.value ??
        data?.battlePass ??
        data ??
        {};

      const rawValue =
        source?.hasBattlePass ??
        source?.has_battle_pass ??
        source?.battlePassActive ??
        source?.active ??
        false;

      const active =
        rawValue === true ||
        rawValue === 1 ||
        rawValue === "1" ||
        String(rawValue)
          .trim()
          .toLowerCase() === "true";

      console.log("[APP BATTLE PASS]", {
        response: data,
        source,
        rawValue,
        active,
      });

      setHasBattlePass(active);
    } catch (error) {
      console.error(
        "[APP BATTLE PASS ERROR]",
        error
      );

      if (!cancelled) {
        setHasBattlePass(false);
      }
    } finally {
      if (!cancelled) {
        setBattlePassLoading(false);
      }
    }
  }

  loadBattlePassStatus();

  return () => {
    cancelled = true;
  };
}, [user?.id]);

useEffect(() => {
  if (!user?.id) return;

  const token = localStorage.getItem('token');
  if (!token) return;

  let deviceId;
  try {
    deviceId = getDeviceId();
  } catch {
    deviceId = `dev_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  }

  fetch(`${process.env.REACT_APP_API_URL}/api/device/register/authed`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Device-Id': deviceId,
    },
  }).catch(() => {});

  fetch(`${process.env.REACT_APP_API_URL}/api/device/flag-human`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Device-Id': deviceId,
    },
  }).catch(() => {});
}, [user?.id]);


useEffect(() => {
  if (!user) return;

  if (
    user.last_gift &&
    user.last_gift_seen !== true &&
    giftModalShownRef.current === false
  ) {
    giftModalShownRef.current = true;
    setGift(user.last_gift);
    setShowGiftModal(true);
  }
}, [user?.id, user?.last_gift, user?.last_gift_seen]);

useEffect(() => {
  const tg = window.Telegram?.WebApp;
  if (!tg || !user) return;

  const tgUser = tg.initDataUnsafe?.user;
  if (!tgUser) return;

  const telegramName = (tgUser.first_name + " " + tgUser.last_name).trim() || tgUser.username;
  
  if (telegramName && telegramName !== user.name) {
    // Update server
    fetch(`${process.env.REACT_APP_API_URL}/api/update-name`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({ name: telegramName })
    })
      .then(() => setUser(prev => ({ ...prev, name: telegramName })))
      .catch(() => {});
  }
}, [user]);


useEffect(() => {
  const tg = window?.Telegram?.WebApp;
  const raw = tg?.initDataUnsafe?.start_param;

  if (!raw || !user?.id) return;

  const challengeMatch =
  /^ch_([0-9a-f-]{36})$/i.exec(
    String(raw).trim()
  );

if (challengeMatch) {
  guardedNavigate(
    "challenges",
    "deeplink",
    {
      initialChallengeId:
        challengeMatch[1],
    }
  );

  return;
}

  const pageName = String(raw).trim().toLowerCase();

  if (pageName === 'profile') {
    setProfileOpen(true);
    return;
  }

  const knownPages = [
    'home',
    'shop',
    'fight',
    'inventory',
    'tasks',
    'friends',
    'leaderboard',
    'marketplace',
    'battle',
    'character',
    'gamepass',
    'yearreview',
    'holidayevent',
    'tappers',
    'fighters',
    'recruiters',
    'arenas',
    'arenaleaderboard',
    'streakevent',
    'tournaments',
    'fortunewheel',
    'hiddenseek',
    'mytapis',
    'blog',
    'memorygame',
    'inventoryhub',
    'gameshub',
    'events',
    'challenges',
    'battlepass',
    'profile',
    'thematicevent',
    'addhomescreen',
  ];

  const leaderboardTabs = ['tappers', 'fighters', 'recruiters'];

if (leaderboardTabs.includes(pageName)) {
  guardedNavigate('leaderboard', 'deeplink', {
    initialView: pageName === 'fighters' ? 'winners' : pageName,
  });
  return;
}

const inventoryTabs = {
  inventory_cards: { tab: "cards" },
  inventory_items: { tab: "items" },
  inventory_merge: { tab: "cards", mergeMode: true },
  inv_cards: { tab: "cards" },
  inv_items: { tab: "items" },
  inv_merge: { tab: "cards", mergeMode: true },
};

if (inventoryTabs[pageName]) {
  guardedNavigate("inventory", "deeplink", inventoryTabs[pageName]);
  return;
}

  const shopTabs = ['cards', 'items', 'coin_1', 'coin_2'];

  if (shopTabs.includes(pageName)) {
    guardedNavigate('shop', 'deeplink', {
      initialView: pageName,
    });
    return;
  }

  if (!knownPages.includes(pageName)) return;

  const pageMap = {
    fortunewheel: 'fortuneWheel',
    hiddenseek: 'hiddenSeek',
    arenaleaderboard: 'arenaLeaderboard',
    memorygame: 'memoryGame',
    gameshub: 'gamesHub',
    battlepass: 'battlePass',
    thematicevent: 'thematicEvent',
    addhomescreen: 'addHomeScreen',
  };

  guardedNavigate(pageMap[pageName] || pageName, 'deeplink');
}, [user?.id]);


  // Redeem promo code from Telegram start_param (only if NOT a page deeplink)
useEffect(() => {
  const tg = window?.Telegram?.WebApp;
  const raw = tg?.initDataUnsafe?.start_param;

  if (!raw || !user?.id) return;

  const token = localStorage.getItem('token');
  if (!token) return;

  const start = String(raw).trim();
  const startLower = start.toLowerCase();

  if (isCampaignStart(start)) return;

  const knownPages = [
    'home',
    'shop',
    'fight',
    'inventory',
    'tasks',
    'friends',
    'leaderboard',
    'marketplace',
    'battle',
    'character',
    'gamepass',
    'holidayevent',
    'tappers',
    'fighters',
    'recruiters',
    'arenas',
    'arenaleaderboard',
    'streakevent',
    'tournaments',
    'fortunewheel',
    'hiddenseek',
    'mytapis',
    'blog',
    'memorygame',
    'inventoryhub',
    'gameshub',
    'events',
    'challenges',
    'battlepass',
    'profile',
    'thematicevent',
  ];

  // If it's a page deeplink, the effect above already handled it
  if (knownPages.includes(startLower)) return;

  // Prevent double fire in the same session (React StrictMode)
  if (promoRedeemInFlightRef.current) return;
  promoRedeemInFlightRef.current = true;

  (async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/me/redeem`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ code: start }),
      });

      // Always read JSON so we can check {status: ...} even when backend returns 200 for invalid
      const data = await res.json().catch(() => ({}));

      // Only refresh /api/me if the promo was actually applied
      if (!res.ok || data?.status !== 'applied') return;

      if (res.ok && Number(data?.awarded?.spins || 0) > 0) {
        guardedNavigate('fortuneWheel', 'promo');
      }

      // Refresh user so gift modal + balances update
      const meRes = await fetch(`${process.env.REACT_APP_API_URL}/api/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (meRes.ok) {
        const me = await meRes.json();
        setUser(me);
      }
    } catch (_) {
    } finally {
      promoRedeemInFlightRef.current = false;
    }
  })();
}, [user?.id]);

const toBool = (v) => {
  if (v === true || v === false) return v;
  if (v === 1 || v === 0) return Boolean(v);
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (s === 'true' || s === '1' || s === 'yes') return true;
    if (s === 'false' || s === '0' || s === 'no') return false;
  }
  return null; // unknown / missing
};

const telegramUserId = Number(
  window?.Telegram?.WebApp?.initDataUnsafe?.user?.id ?? NaN
);

const blockerBypassed =
  Number.isFinite(telegramUserId) &&
  BLOCKER_BYPASS_TELEGRAM_IDS.some((id) => Number(id) === telegramUserId);

const shouldShowBlocker = blockerEnabled && !blockerBypassed;
const loginBlocked = blockerEnabled && !blockerBypassed;

useEffect(() => {
  if (isLoading) return;
  if (!user?.side) return;
  if (!shouldOpenAllSeasonModalRef.current) return;

  shouldOpenAllSeasonModalRef.current = false;
  setSeasonChangeMode(isNewUserRef.current ? 'newUser' : 'all');
  setShowSeasonChangeModal(true);

  isNewUserRef.current = false;
}, [isLoading, user?.side]);

// GLOBAL: Force side selection regardless of which table/page user enters
useEffect(() => {
  if (!user?.id) return;

  if (!user?.side) {
    shouldOpenAllSeasonModalRef.current = true;
    setShowGlobalSideModal(true);
  } else {
    setShowGlobalSideModal(false);
  }
}, [user?.id, user?.side]);

// Randomize initial highlight whenever modal opens (same behavior as TapGame)
useEffect(() => {
  if (!showGlobalSideModal) return;
  setSelectedSideIndex(Math.random() < 0.5 ? 0 : 1);
}, [showGlobalSideModal]);

const sides = [
  {
    key: 'bad',
    title: 'Moon Anubis',
    emoji: '',
    description: 'You thrive in chaos, power, and domination.',
    image: '/choosebg.png',
    tapimage: '/v2/side_bad.png?v=2',
  },
  {
    key: 'good',
    title: 'Sun Pharaohs',
    emoji: '',
    description: 'You stand for peace, honor, and harmony.',
    image: '/choosebg.png',
    tapimage: '/v2/side_good.png?v=2',
  },
];

const handleSelectSideGlobal = async (chosenSide) => {
  if (isChoosingSide) return;
  setIsChoosingSide(true);
  setSideErrorMessage('');

  const prevSide = user?.side ?? null;

  // optimistic update & close
  setUser((prev) => ({ ...prev, side: chosenSide }));
  setShowGlobalSideModal(false);

  try {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/select-side`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify({ side: chosenSide }),
    });

    const data = await res.json();

    if (!res.ok) {
      // revert and keep modal open
      setUser((prev) => ({ ...prev, side: prevSide }));
      setShowGlobalSideModal(true);
      setSideErrorMessage(data?.error || 'Failed to select side');
      return;
    }

    // best effort: rehydrate
    try {
      const meRes = await fetch(`${process.env.REACT_APP_API_URL}/api/me`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (meRes.ok) {
        const updatedUser = await meRes.json();
        setUser(updatedUser);
      }
    } catch (_) {}
  } catch (err) {
    setUser((prev) => ({ ...prev, side: prevSide }));
    setShowGlobalSideModal(true);
    setSideErrorMessage('Something went wrong. Please try again.');
  } finally {
    setIsChoosingSide(false);
  }
};

const isAnyBlockingModalOpen =
  profileOpen ||
  showWhitelistModal ||
  showEarlyLockModal ||
  showDailyModal ||
  showWalletModal ||
  showFeedbackModal ||
  showGiftModal ||
  showTournamentsModal ||
  showTournamentTwiceDailyModal ||
  showWarning ||
  showBlocked ||
  showCmsModal ||
  showGlobalSideModal ||
  showSeasonChangeModal ||
  showTwiceDailyModal;

  useEffect(() => {
  if (isLoading) return;
  if (!user?.id || isGated()) return;
  if (isAnyBlockingModalOpen) return;
  if (!TOURNAMENT_MODAL_ENABLED) return;

  const timer = setTimeout(() => {
    try {
      const now = new Date();

      const yyyy = now.getUTCFullYear();
      const mm = String(now.getUTCMonth() + 1).padStart(2, '0');
      const dd = String(now.getUTCDate()).padStart(2, '0');
      const utcDay = `${yyyy}-${mm}-${dd}`;

      const hour = now.getUTCHours();
      const slot = hour < 12 ? 'am' : 'pm';

      const tournamentId = ACTIVE_TOURNAMENT_ID || 'active';
      const guardKey = `tournamentTwiceDailyModalShown:${user.id}:${tournamentId}:${utcDay}:${slot}`;

      if (localStorage.getItem(guardKey) === '1') return;

      localStorage.setItem(guardKey, '1');
      tournamentModalOpenedThisVisitRef.current = true;
      setShowTournamentTwiceDailyModal(true);
    } catch (e) {
      console.warn('Tournament twice daily modal auto-open failed', e);
    }
  }, 700);

  return () => clearTimeout(timer);
}, [
  isLoading,
  user?.id,
  whitelistEnabled,
  TOURNAMENT_MODAL_ENABLED,
  ACTIVE_TOURNAMENT_ID,
  profileOpen,
  showWhitelistModal,
  showEarlyLockModal,
  showDailyModal,
  showWalletModal,
  showFeedbackModal,
  showGiftModal,
  showTournamentsModal,
  showTournamentTwiceDailyModal,
  showWarning,
  showBlocked,
  showCmsModal,
  showGlobalSideModal,
  showSeasonChangeModal,
  showTwiceDailyModal,
]);

useEffect(() => {
  if (isLoading) return;
  if (!user?.id || isGated()) return;
  if (isAnyBlockingModalOpen) return;

  const timer = setTimeout(async () => {
  try {
    // Se o modal de torneio abriu nesta visita,
    // o Gamepass NÃO pode abrir junto.
    if (tournamentModalOpenedThisVisitRef.current) return;

    const now = new Date();

    const yyyy = now.getUTCFullYear();
    const mm = String(now.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(now.getUTCDate()).padStart(2, '0');
    const utcDay = `${yyyy}-${mm}-${dd}`;

    const hour = now.getUTCHours();
    const slot = hour < 12 ? 'am' : 'pm';

    const tournamentId = ACTIVE_TOURNAMENT_ID || 'active';
    const tournamentGuardKey =
      `tournamentTwiceDailyModalShown:${user.id}:${tournamentId}:${utcDay}:${slot}`;

    // Se torneio está ativo e ainda NÃO mostrou o torneio neste slot,
    // o Gamepass também NÃO pode abrir. O effect do torneio cuida disso.
    if (
      TOURNAMENT_MODAL_ENABLED &&
      localStorage.getItem(tournamentGuardKey) !== '1'
    ) {
      return;
    }

    const DEBUG_TWICE_DAILY_MODAL = false;

const guardKey = `twiceDailyModalShown:${user.id}:${utcDay}:${slot}`;

if (!DEBUG_TWICE_DAILY_MODAL && localStorage.getItem(guardKey) === '1') return;

const ok = await openTwiceDailyModal();

if (ok && !DEBUG_TWICE_DAILY_MODAL) {
  localStorage.setItem(guardKey, '1');
}
    } catch (e) {
      console.warn('Twice daily modal auto-open failed', e);
    }
  }, 800);

  return () => clearTimeout(timer);
}, [
  isLoading,
  user?.id,
  whitelistEnabled,
  showWhitelistModal,
  profileOpen,
  showEarlyLockModal,
  showDailyModal,
  showWalletModal,
  showFeedbackModal,
  showGiftModal,
  showTournamentsModal,
  showWarning,
  showBlocked,
  showCmsModal,
  showGlobalSideModal,
  showSeasonChangeModal,
  showTwiceDailyModal,
]);

const openTwiceDailyModal = async () => {
  if (needsHumanCheck) return false;

  setTwiceDailyModalData({
    title: 'New Feature',
    message: 'Here goes your new message, promo, event, reminder, whatever you want.',
  });

  setShowTwiceDailyModal(true);
  return true;
};

//MODAL
//useEffect(() => {
  //if (!user?.id) return;

  //setTwiceDailyModalData({
    //title: 'New Feature',
    //message: 'Here goes your new message, promo, event, reminder, whatever you want.',
  //});

  //setShowTwiceDailyModal(true);
//}, [user?.id]);

//useEffect(() => {
//  const token = localStorage.getItem('token');
//  if (!user?.id || !token || isGated()) return;
//
//  (async () => {
//    try {
//      // 1) Get server "now" (we already return serverNow from /api/status)
//      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/status`, {
//        headers: { Authorization: `Bearer ${token}` },
 //     });
 //     const data = await res.json().catch(() => ({}));
//
//      // Same helper you already have in this file:
//      // const getServerNowMs = (res, data) => { ... }
//      const serverNowMs = getServerNowMs(res, data);
//
//      if (serverNowMs == null) {
//        // If we can’t prove server time, don’t auto-open (avoid abuse)
//        return;
//      }

      // 2) Compute *server* calendar date in your canonical TZ
      //const tz = 'America/Sao_Paulo';
      //const serverToday = new Date(serverNowMs).toLocaleDateString('en-CA', { timeZone: tz });
 //     const serverToday = new Date(serverNowMs).toISOString().slice(0, 10);

      // 3) Use server date in the guard key
 //     const guardKey = `dailyAutoShown:${user.id}:${serverToday}`;
 //     if (localStorage.getItem(guardKey) === '1') return;

      // 4) Open the modal; only set the guard if backend says it opened OK
 //     setTimeout(async () => {
 //       const ok = await openDailyRewards();
 //       if (ok) localStorage.setItem(guardKey, '1');
 //       else console.warn('Auto daily failed; will try again later.');
 //     }, 250);
 //   } catch (e) {
 //     console.warn('Daily auto-open: failed to fetch server time', e);
 //   }
 // })();
//}, [user?.id, whitelistEnabled]); // keep deps same as before


  const handleLogout = () => { localStorage.removeItem('token'); setUser(null); };

  //useEffect(() => {
  //setWlTargetIso('2025-11-03T17:00:00+01:00');
//}, []);

useEffect(() => {
  const tg = window?.Telegram?.WebApp;
  const raw = tg?.initDataUnsafe?.start_param;

  if (!raw || !user?.id) return;

  // only campaign codes here
  if (!isCampaignStart(raw)) return;

  const token = localStorage.getItem('token');
  if (!token) return;

  const code = parseCampaignCode(raw);
  if (!code) return;

  // prevent double fire in StrictMode / rerenders
  const guardKey = `cmpTracked:${user.id}:${code}`;
  if (localStorage.getItem(guardKey) === '1') return;
  localStorage.setItem(guardKey, '1');

  fetch(`${process.env.REACT_APP_API_URL}/api/track/campaign`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      code,                 // "tiktok_2025w01"
      rawStartParam: raw,   // "cmp_tiktok_2025w01"
      source: 'telegram',   // optional
    }),
  }).catch(() => {});
}, [user?.id]);


useEffect(() => {
  const token = localStorage.getItem('token');
  if (!user?.id || !token) return;

  (async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      const serverNowMs = getServerNowMs(res, data);
      if (serverNowMs == null) return;

      //const tz = 'America/Sao_Paulo';
      //const serverDay = new Date(serverNowMs).toLocaleDateString('en-CA', { timeZone: tz });
      const serverDay = new Date(serverNowMs).toISOString().slice(0, 10);

      const guardKey = `hb:${user.id}:${serverDay}`;
      if (localStorage.getItem(guardKey) === '1') return;

      await fetch(`${process.env.REACT_APP_API_URL}/api/track/heartbeat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ serverDay }),
      });

      localStorage.setItem(guardKey, '1');
    } catch {}
  })();
}, [user?.id]);

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const hasGiftItems = Array.isArray(gift?.items) && gift.items.some(it => {
  const qty = Number(it?.quantity ?? 0);
  const id = String(it?.itemId ?? '').trim();
  return id && Number.isFinite(qty) && qty > 0;
});

const hasAnyReward =
  toNum(gift?.sparks) > 0 ||
  toNum(gift?.runes) > 0 ||
  toNum(gift?.credits) > 0 ||
  toNum(gift?.tapi) > 0 ||
  toNum(gift?.spins) > 0 ||
  (gift?.rarity && gift.rarity !== 'None') ||
  hasGiftItems;

const giftMessage = String(gift?.message ?? '').trim();


useEffect(() => {
  if (!cmsModalsCfg || cmsModalsCfg.length === 0) return;
  if (!user?.id) return;

  const anyOtherModalOpen =
    profileOpen ||
    showWhitelistModal ||
    showEarlyLockModal ||
    showDailyModal ||
    showWalletModal ||
    showFeedbackModal ||
    showGiftModal ||
    showTournamentsModal ||
    showWarning ||
    showBlocked;

  if (anyOtherModalOpen) return;
  if (showCmsModal) return;

  const VISITS_KEY = 'app_visits_count';
  const visits = Number(localStorage.getItem(VISITS_KEY) || '0') + 1;
  localStorage.setItem(VISITS_KEY, String(visits));

  const tgStart = window?.Telegram?.WebApp?.initDataUnsafe?.start_param;
  const params = new URLSearchParams(window.location.search);
  const startappParam = String(tgStart || params.get('startapp') || '').trim();

  const visitName =
    visits === 1 ? 'first' :
    visits === 2 ? 'second' :
    visits === 3 ? 'third' : 'always';

  const shouldShow = (m) => {
    if (!m || m.enabled === false) return false;

    if (m?.id && cmsDismissedThisBootRef.current.has(String(m.id))) return false;

    const trig = m.trigger || {};
    const visit = trig.visit || 'always';

    if (visit !== 'always' && visit !== visitName) return false;

    if (trig.startapp && String(trig.startapp).trim() !== '') {
      if (String(trig.startapp).trim() !== startappParam) return false;
    }

    if (trig.sparksLt !== null && trig.sparksLt !== undefined && trig.sparksLt !== '') {
      const limit = Number(trig.sparksLt);
      if (Number.isFinite(limit)) {
        const userSparks = Number(user?.sparks ?? 0);
        if (!(userSparks < limit)) return false;
      }
    }

    if (trig.runesLt !== null && trig.runesLt !== undefined && trig.runesLt !== '') {
      const limit = Number(trig.runesLt);
      if (Number.isFinite(limit)) {
        const userRunes = Number(user?.runes ?? 0);
        if (!(userRunes < limit)) return false;
      }
    }

    if (trig.oneTime) {
      const key = `cms_modal_seen_${m.id}`;
      if (localStorage.getItem(key) === '1') return false;
    }

    return true;
  };

  const candidates = cmsModalsCfg
    .filter(shouldShow)
    .sort((a, b) => (Number(b.priority || 0) - Number(a.priority || 0)));

  const chosen = candidates[0];
  if (!chosen) return;

  if (chosen?.trigger?.oneTime) {
    localStorage.setItem(`cms_modal_seen_${chosen.id}`, '1');
  }

  setCmsModal(chosen);
  setShowCmsModal(true);
}, [
  cmsModalsCfg,
  user?.id,
  user?.sparks,
  user?.runes,
  profileOpen,
  showCmsModal,
  showWhitelistModal,
  showEarlyLockModal,
  showDailyModal,
  showWalletModal,
  showFeedbackModal,
  showGiftModal,
  showTournamentsModal,
  showWarning,
  showBlocked,
]);

const [specialEventEnabled, setSpecialEventEnabled] = useState(false);
const [holidayEnabled, setHolidayEnabled] = useState(false);
const [activeEvent, setActiveEvent] = useState(null);
const [eventStatusLoaded, setEventStatusLoaded] = useState(false);
const [activeThematicEvent, setActiveThematicEvent] = useState(null);
const [thematicEventStatusLoaded, setThematicEventStatusLoaded] = useState(false);

useEffect(() => {
  if (!user?.id) return;

  const token = localStorage.getItem("token");
  if (!token) return;

  let cancelled = false;

  const loadActiveEvent = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/events/current`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await res.json().catch(() => ({}));

      if (cancelled) return;

      const event =
        res.ok
          ? data?.event || data?.currentEvent || null
          : null;

      setActiveEvent(event);
      setSpecialEventEnabled(Boolean(event));
    } catch {
      if (!cancelled) {
        setActiveEvent(null);
        setSpecialEventEnabled(false);
      }
    } finally {
      if (!cancelled) {
        setEventStatusLoaded(true);
      }
    }
  };

  loadActiveEvent();

  return () => {
    cancelled = true;
  };
}, [user?.id]);

useEffect(() => {
  if (!user?.id) {
    setActiveThematicEvent(null);
    setThematicEventStatusLoaded(false);
    return;
  }

  const token = localStorage.getItem("token");

  if (!token) {
    setActiveThematicEvent(null);
    setThematicEventStatusLoaded(false);
    return;
  }

  let cancelled = false;

  const loadActiveThematicEvent = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/thematic-events/current`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await res
        .json()
        .catch(() => ({}));

      if (cancelled) return;

      const thematicEvent =
        res.ok &&
        data?.active === true &&
        data?.event
          ? data.event
          : null;

      setActiveThematicEvent(
        thematicEvent
      );
    } catch (err) {
      console.error(
        "[APP THEMATIC EVENT STATUS]",
        err
      );

      if (!cancelled) {
        setActiveThematicEvent(null);
      }
    } finally {
      if (!cancelled) {
        setThematicEventStatusLoaded(true);
      }
    }
  };

  loadActiveThematicEvent();

  return () => {
    cancelled = true;
  };
}, [user?.id]);

useEffect(() => {
  if (!user?.id) return;

  let cancelled = false;

  const checkTaskRewards = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/tasks`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-TG-INITDATA":
              window.Telegram?.WebApp?.initData || "",
          },
        }
      );

      const data = await res.json().catch(() => ({}));

      const tasks = Array.isArray(data)
        ? data
        : Array.isArray(data?.tasks)
        ? data.tasks
        : [];

      const claimDelayMs = 60 * 1000;

      const rewardReady = tasks.some((task) => {
        if (task?.claimedAt || task?.claimed_at) return false;

        const visitedAt =
          task?.visitedAt || task?.visited_at;

        if (!visitedAt) return false;

        const visitedMs = new Date(visitedAt).getTime();

        if (!Number.isFinite(visitedMs)) return false;

        return Date.now() - visitedMs >= claimDelayMs;
      });

      if (!cancelled) {
        setHasTaskReward(rewardReady);
      }
    } catch (error) {
      console.error("Failed to check task rewards:", error);

      if (!cancelled) {
        setHasTaskReward(false);
      }
    }
  };

  checkTaskRewards();

  return () => {
    cancelled = true;
  };
}, [user?.id, page]);

useEffect(() => {
  if (!user?.id) return;
  let cancelled = false;

  const loadHolidayStatus = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/holiday-event/status`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const json = await res.json().catch(() => ({}));

      console.log("[HOLIDAY API RESPONSE RAW]", json);

      if (!cancelled) {
        setHolidayEnabled(!!json?.enabled);
      }
    } catch {
      if (!cancelled) {
        setHolidayEnabled(false);
      }
    }
  };

  loadHolidayStatus();

  return () => {
    cancelled = true;
  };
}, [user?.id]);


const [runeHuntersEnabled, setRuneHuntersEnabled] = useState(false);

const [hasTaskReward, setHasTaskReward] = useState(false);

const hasLiveGame =
  Boolean(specialEventEnabled) ||
  Boolean(holidayEnabled) ||
  Boolean(runeHuntersEnabled) ||
  Boolean(TOURNAMENT_MODAL_ENABLED);

useEffect(() => {
  if (!user?.id) return;
  let cancelled = false;

  const loadRuneHuntersStatus = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/battle/streak`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const json = await res.json().catch(() => ({}));

      const toTs = (v) => {
        if (!v) return null;
        const t = new Date(v).getTime();
        return Number.isFinite(t) ? t : null;
      };

      const startTs =
        toTs(json?.startDate) ??
        toTs(json?.start_date) ??
        toTs(json?.startsAt) ??
        toTs(json?.starts_at);

      const endTs =
        toTs(json?.endDate) ??
        toTs(json?.end_date) ??
        toTs(json?.endsAt) ??
        toTs(json?.ends_at);

      const now = Date.now();

      const activeByDate =
        startTs != null && endTs != null ? now >= startTs && now <= endTs : null;

      const activeFromApi =
        json?.active ?? json?.isActive ?? json?.enabled ?? json?.eventActive;

      const activeFlag =
        typeof activeFromApi === "boolean"
          ? activeFromApi
          : activeFromApi === 1 || String(activeFromApi).toLowerCase() === "true";

      const milestonesArr = Array.isArray(json?.milestones) ? json.milestones : [];
      const cycle = Number(json?.cycleSize ?? 0);
      const configured = milestonesArr.length > 0 && cycle > 0;

      const active =
        activeByDate != null ? activeByDate : activeFromApi != null ? activeFlag : configured;

      if (!cancelled) {
        setRuneHuntersEnabled(!!active);
      }
    } catch {
      if (!cancelled) {
        setRuneHuntersEnabled(false);
      }
    }
  };

  loadRuneHuntersStatus();

  return () => {
    cancelled = true;
  };
}, [user?.id]);

const UNBAN_IMAGE_BASE_URL = 'https://www.taptopia.io/unban.php';

const getUnbanImageUrl = () => {
  const userId = encodeURIComponent(String(user?.id || ''));
  return `${UNBAN_IMAGE_BASE_URL}?userId=${userId}&t=${unbanImgTick}`;
};

const reloadUnbanImage = () => {
  setUnbanCode('');
  setUnbanError('');
  setUnbanImgTick(Date.now());
};

const submitUnbanCode = async () => {
  const code = String(unbanCode || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

  if (!code) {
    setUnbanError('Enter the code from the image.');
    return;
  }

  try {
    setUnbanBusy(true);
    setUnbanError('');

    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/unban/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify({ code }),
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(data?.error || 'Invalid code.');
    }

    setUser((prev) => ({
      ...prev,
      is_blocked: false,
    }));

    setShowBlocked(false);
    setUnbanCode('');
    setUnbanError('');
  } catch (err) {
    setUnbanError(err?.message || 'Invalid code.');
    reloadUnbanImage();
  } finally {
    setUnbanBusy(false);
  }
};

  // ---------- Render ALWAYS mounts SafeAreaBinder ----------
  return (
  <div
    className={`app ${page === "thematicEvent" ? "thematic-event-page" : ""}`}
    style={
      page === "thematicEvent"
        ? {
            backgroundImage: `
              url("/theme_2_bg.png"),
              url("/theme_2_bg.png")
            `,
            backgroundSize: `
              100% auto,
              100% auto
            `,
            backgroundPosition: `
              center top,
              center top
            `,
            backgroundRepeat: `
              no-repeat,
              repeat-y
            `,
            minHeight: "100vh",
          }
        : undefined
    }
  >
    <SafeAreaBinder disableWhile={isLoading} debug={false} alertOnce={false} />

      {mobileGuardEnabled && !isMobile ? (
        <div
          style={{
            position: 'fixed', inset: 0, display: 'grid', placeItems: 'center', textAlign: 'center',
            background: "linear-gradient(180deg, rgba(0,0,0,0.75), rgba(0,0,0,0.85)), url('/tap_bg_1.png') center / cover no-repeat",
            color: '#fff', padding: 24, zIndex: 999999,
          }}
        >
          <div style={{ maxWidth: 520 }}>
            <img src="/finger.png" alt="" style={{ height: 80, marginBottom: 12 }} />
            <h2 style={{ margin: 0, fontSize: 28 }}>Mobile Only</h2>
            <p style={{ opacity: 0.9, marginTop: 8, lineHeight: 1.5 }}>
              This game is optimized for mobile. <br />
              Please open it using the Telegram app on your phone.
            </p>
          </div>
        </div>
     ) : isLoading ? (
  shouldShowBlocker ? (
    <Blocker
      targetDate={BLOCKER_TARGET_DATE}
      onContinue={() => {
        setIsLoading(false);

        setAudioUnlocked(true);

        const a = bgmRef.current;
        if (a && soundEnabled) {
          a.play().catch(() => {});
        }
      }}
    />
  ) : (
    <SplashScreen
      onPlay={() => {
        setIsLoading(false);

        setAudioUnlocked(true);

        const a = bgmRef.current;
        if (a && soundEnabled) {
          a.play().catch(() => {});
        }
      }}
    />
  )

            ) : !user ? (
        loginBlocked ? (
          <Blocker targetDate={BLOCKER_TARGET_DATE} />
        ) : (
          <Login onLogin={(userData, meta) => {
            setUser(userData);

            // Keep App's whitelist flag fresh (authoritative from login/me)
            if (typeof meta?.whitelistEnabled === 'boolean') {
              setWhitelistEnabled(meta.whitelistEnabled);
            }

            // Show welcome ONLY when it's truly a new signup AND whitelist is on
            const createdNew = (meta?.new ?? meta?.isNew) === true;
            isNewUserRef.current = createdNew;

            if (createdNew && meta?.whitelistEnabled === true) {
              //setWelcomeName(userData?.name || '');
              //setShowWelcomeModal(true);
            }
          }} />
        )
      ) : (
  <div>
          
<Header
  user={user}
  onLogout={handleLogout}
  isMenuOpen={isMenuOpen}
  onNavigate={onNavigate}
  onToggleMenu={toggleMenu}
  onOpenCurrencyPurchase={openCurrencyPurchaseModal}
  whitelistEnabled={whitelistEnabled}
  onWhitelistBlocked={showGateModal}
  onOpenProfile={() => setProfileOpen(true)}
/>

<ProfileModal
  open={profileOpen}
  user={user}
  setUser={setUser}
   hasBattlePass={hasBattlePass}
  battlePassLoading={battlePassLoading}
  onClose={() => setProfileOpen(false)}
  onNavigate={onNavigate}
  onInviteFriend={() => {
    setProfileOpen(false);
    onNavigate("friends");
  }}
  onIncreaseLevel={() => {
    setProfileOpen(false);
    onNavigate("home");
  }}
  onOpenDefenseDeck={() => {
    setProfileOpen(false);
    onNavigate("defenseDeck");
  }}
  onOpenShop={openCurrencyPurchaseModal}
/>

<CurrencyPurchaseModal
  open={currencyModalOpen}
  currency={currencyModalType}
  user={user}
  setUser={setUser}
  onClose={() => setCurrencyModalOpen(false)}
/>


          {isMenuOpen && (
            <div className="modal_box">

              <div className="modal_title">Navigation<button  onClick={() => setIsMenuOpen(false)} className="modal_close"/></div>
              <motion.div className="modal_body" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>
              <div className="modal_inside_3">

<li style={{display: "block"}}
  onClick={() => {
    setIsMenuOpen(false);
    gate('blog', () => guardedNavigate('blog', 'menu'));
  }}
>
  <b
    style={{
      float: 'left',
      width: '48px',
      height: '48px',
      display: 'inline-block',
      backgroundImage: "url('/v2/menu_1.png')",
      backgroundSize: '50%',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center center',
    }}
  />
  TAPTOPIA NEWS
</li>

                 <li style={{display: "block"}} onClick={() => { setIsMenuOpen(false); if (user?.needs_human_check) {
      onNavigate('humancheck', {
        returnTo: pageRef.current || 'home',
      });
      return;
    } gate('daily-rewards', () => openDailyRewards()); }}>
                  <b style={{
      float: 'left',
      width: '48px',
      height: '48px',
      display: 'inline-block',
      backgroundImage: "url('/v2/menu_2.png')",
      backgroundSize: '50%',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center center',
    }}
      />
                  DAILY REWARDS
                </li>
<li
  style={{display: 'block'}} onClick={() => {
    setIsMenuOpen(false);
    gate('inventory', () => guardedNavigate('inventoryHub', 'menu'));
  }}
>
  <b
    style={{
      float: 'left',
      width: '48px',
      height: '48px',
      display: 'inline-block',
      backgroundImage: "url('/v2/menu_3.png')",
      backgroundSize: '50%',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center center',
    }}
  />
  INVENTORY
</li>

<li
  style={{display: "block"}} onClick={() => {
    setIsMenuOpen(false);
    gate('battlePass', () => guardedNavigate('battlePass', 'menu'));
  }}
>
  <b
    style={{
      float: 'left',
      width: '48px',
      height: '48px',
      display: 'inline-block',
      backgroundImage: "url('/v2/menu_4.png')",
      backgroundSize: '50%',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center center',
    }}
  />
  OG PASS PLUS
</li>

<li onClick={() => { setIsMenuOpen(false); gate('tournaments', () => guardedNavigate('tournaments', 'menu')); }}>
                  <b
    style={{
      float: 'left',
      width: '48px',
      height: '48px',
      display: 'inline-block',
      backgroundImage: "url('/v2/menu_5.png')",
      backgroundSize: '50%',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center center',
    }}
  />
                  TOURNAMENTS
                </li>

                <li style={{display: 'none'}}
  onClick={() => {
    setIsMenuOpen(false);
    gate(
      'tournamentsV2',
      () => guardedNavigate('tournamentsV2', 'menu')
    );
  }}
>
  <b
    style={{
      float: 'left',
      width: '48px',
      height: '48px',
      display: 'noneinline-block',
      backgroundImage: "url('/v2/menu_5.png')",
      backgroundSize: '50%',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center center',
    }}
  />

  TOURNAMENTS V2 TEST
</li>

               <li onClick={() => { setIsMenuOpen(false); gate('leaderboard', () => guardedNavigate('leaderboard', 'menu')); }}>
                  <b
    style={{
      float: 'left',
      width: '48px',
      height: '48px',
      display: 'inline-block',
      backgroundImage: "url('/v2/menu_6.png')",
      backgroundSize: '50%',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center center',
    }}
  />
                  LEADERBOARDS
                </li>
                

<li
  style={{ display: "none" }}
  onClick={() => {
    setIsMenuOpen(false);
    guardedNavigate("guildBrowser", "menu");
  }}
>
  <b
    style={{
      float: "left",
      width: "48px",
      height: "48px",
      display: "inline-block",
      backgroundImage: "url('/fightlog.png')",
      backgroundSize: "50%",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center center",
    }}
  />

  GUILDS
</li>


                <li style={{color: '#fff'}}
  onClick={() => {
    const tg = window?.Telegram?.WebApp;

    setIsMenuOpen(false);

    if (tg) {
      tg.openTelegramLink('https://t.me/taptopia_io');
    } else {
      window.open('https://t.me/taptopia_io', '_blank');
    }
  }}
>
  <b
    style={{
      float: 'left',
      width: '48px',
      height: '48px',
      display: 'inline-block',
      backgroundImage: "url('/v2/menu_7.png')",
      backgroundSize: '50%',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center center',
    }}
  />
  CHAT
</li>

                <li style={{color: '#fff'}} onClick={() => { setIsMenuOpen(false); openFeedbackModal(); }}>
                  <b
    style={{
      float: 'left',
      width: '48px',
      height: '48px',
      display: 'inline-block',
      backgroundImage: "url('/v2/menu_8.png')",
      backgroundSize: '50%',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center center',
    }}
  />
                  FEEDBACK
                </li>


                <li style={{color: '#fff'}} onClick={() => { setIsMenuOpen(false); setShowSettings(true); }}>
                <b
    style={{
      float: 'left',
      width: '48px',
      height: '48px',
      display: 'inline-block',
      backgroundImage: "url('/v2/menu_9.png')",
      backgroundSize: '50%',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center center',
    }}
  />
                  SETTINGS
                </li>


                {EMAIL_LOGIN_ENABLED && (
                  <li
                    onClick={() => { setIsMenuOpen(false); handleLogout(); }}
                    style={{ color: '#ff6b6b' }}
                  >
                    <b style={{
                      verticalAlign: 'middle', marginTop: '-6px', marginRight: '12px',
                      width: '32px', height: '32px', display: 'inline-block',
                      backgroundImage: "url('/logout.png')", backgroundSize: '32px 32px',
                      backgroundRepeat: 'no-repeat', backgroundPosition: 'center center',
                    }} />
                    LOGOUT
                  </li>
                )}

                

                <div style={{float: "left", width: "100%", marginTop: 15,marginBottom: 0}}>
                <a href="#"><img src="./discord.png" style={{display:'none', width: '36px', height: '28px', marginLeft: 12, marginRight: 12}} alt="Discord" /></a>
                <a href="https://t.me/taptopia_io" target="_new"><img src="./v2/telegram.png" style={{ width: '28px', height: '28px', marginLeft: 12, marginRight: 12}} alt="Telegram" /></a>
                <a href="https://x.com/taptopia_io" target="_new"><img src="./v2/x.png" style={{ width: '28px', height: '28px', marginLeft: 12, marginRight: 12}} alt="X" /></a>
                </div>
                </div>

              </motion.div>

              
            
            </div>
          )}

          <main className="main-content">

{page === 'events' && (
  <EventsPage
    user={user}
    setUser={setUser}
    onNavigate={onNavigate}
    initialView={pageParams?.initialView}
  />
)}

          {page === 'humancheck' && (
  <HumanCheckpoint
    user={user}
    setUser={setUser}
    onBack={() => {
      const rt = pageParams?.returnTo || "home";
      const rp = pageParams?.returnParams || {};
      onNavigate(rt, rp);
    }}
  />
)}

{page === "addHomeScreen" && (
  <AddHomeScreen
    user={user}
    setUser={setUser}
    onNavigate={onNavigate}
  />
)}

{page === "fortuneWheel" && (
  <FortuneWheel
    user={user}
    setUser={setUser}
    onNavigate={onNavigate}
    sfxEnabled={sfxEnabled}
    sfxVolume={sfxVolume}
    onClose={() => onNavigate("home")}
    onSpinResult={(prize) => {
    }}
  />
)}

{page === "hiddenSeek" && (
  <HiddenSeek
    user={user}
    setUser={setUser}
    onNavigate={onNavigate}
    onClose={() => onNavigate("home")}
  />
)}

            {page === 'home' && Number.isFinite(Number(user?.level)) ? (
   <TapGame
     user={user}
    setShowSeasonChange={(mode) => {
      setSeasonChangeMode(mode);
      setShowSeasonChangeModal(true);
    }}
     setUser={setUser}
     onNavigate={onNavigate}
     whitelistEnabled={isGated()}
     onWhitelistBlocked={(action) => showGateModal(action)}
     isGated={isGated}
     soundEnabled={soundEnabled}
     sfxVolume={sfxVolume}
     sfxEnabled={sfxEnabled}
     gate={gate}
     tournamentBadgeOn={tournamentBadgeOn}
    tournamentEndDate={TOURNAMENT_END_DATE}
    tournamentType={TOURNAMENT_TYPE}
     tournamentEnabled={TOURNAMENT_MODAL_ENABLED}
      tournamentStartDate={TOURNAMENT_START_DATE}
      activeTournamentId={ACTIVE_TOURNAMENT_ID}
      onOpenShop={(which) => {gate('shop', () => {setShopView(which);setPage('shop');});}}
   />
 ) : page === 'home' ? (
   <div style={{textAlign:'center', color:'#fff'}}>Loading…</div>
 ) : null}

{page === 'arenaLeaderboard' && (
  <ArenaLeaderboard
    user={user}
    onNavigate={onNavigate}
    openedVia={navSource}
    initialView={pageParams?.initialView}
    arenaId={pageParams?.arenaId}
    arenaIndex={pageParams?.arenaIndex}
    viewOnly={pageParams?.viewOnly}
  />
)}

{page === 'mytapis' && (
  <MyTapisPage user={user} onNavigate={onNavigate} />
)}

{page === 'blog' && (
  <Blog
    user={user}
    setUser={setUser}
    onNavigate={onNavigate}
  />
)}

{page === "arenas" && (
  <Arenas
    user={user}
    setUser={setUser}
    setShowSeasonChange={(mode) => {
      setSeasonChangeMode(mode);
      setShowSeasonChangeModal(true);
    }}
    onOpenProfile={() => setProfileOpen(true)}
     hasBattlePass={hasBattlePass}
    battlePassLoading={battlePassLoading}
    selectedCardId={selectedCardId}
    arenaIndex={pageParams?.arenaIndex}
    arenaId={pageParams?.arenaId}
    openedVia={pageParams?.openedVia}
    viewOnly={pageParams?.viewOnly}
    tournamentEnabled={TOURNAMENT_MODAL_ENABLED}
    tournamentBadgeOn={tournamentBadgeOn}
    hasTournament={hasTournament}
    tournamentStartDate={TOURNAMENT_START_DATE}
    tournamentEndDate={TOURNAMENT_END_DATE}
    activeTournamentId={ACTIVE_TOURNAMENT_ID}
    holidayEnabled={holidayEnabled}
    activeEvent={activeEvent}
    eventStatusLoaded={eventStatusLoaded}
    runeHuntersEnabled={runeHuntersEnabled}
    activeThematicEvent={activeThematicEvent}
    thematicEventStatusLoaded={thematicEventStatusLoaded}
    onNavigate={(p, params = {}) => guardedNavigate(p, "Arenas", params)}
    onBack={() => guardedNavigate("home", "ArenasBack")}
  />
)}

{page === "guilds" && (
  <Guilds
    user={user}
    setUser={setUser}
    onNavigate={onNavigate}
  />
)}

{page === "guildBrowser" && (
  <GuildBrowser
    user={user}
    onNavigate={onNavigate}
  />
)}

{page === "gamesHub" && (
  <GamesHub
    user={user}
     hasBattlePass={hasBattlePass}
     onOpenProfile={() => setProfileOpen(true)}
    battlePassLoading={battlePassLoading}
    tournamentEnabled={TOURNAMENT_MODAL_ENABLED}
    tournamentBadgeOn={tournamentBadgeOn}
    hasTournament={hasTournament}
    tournamentStartDate={TOURNAMENT_START_DATE}
    tournamentEndDate={TOURNAMENT_END_DATE}
    holidayEnabled={holidayEnabled}
    runeHuntersEnabled={runeHuntersEnabled}
    onOpenDailyRewards={openDailyRewards}
    onNavigate={(p, params = {}) =>
      guardedNavigate(p, "GamesHub", params)
    }
  />
)}

{page === 'shop' && (
  <Shop
    user={user}
    setUser={setUser}
    onNavigate={onNavigate}
    initialView={pageParams?.initialView ?? shopView}
  />
)}

            {page === 'fight' && <Fight user={user} setUser={setUser} onNavigate={onNavigate} setSelectedCardId={setSelectedCardId} openedVia={pageParams?.openedVia ?? navSource} returnTo={pageParams?.returnTo} returnParams={pageParams?.returnParams} onOpenShop={(which) => {gate('shop', () => {setShopView(which);setPage('shop');});}} />}

{page === 'inventoryHub' && (
  <InventoryHub
    user={user}
    setUser={setUser}
    onNavigate={onNavigate}
  />
)}

{page === 'defenseDeck' && (
  <DefenseDeck
    user={user}
    setUser={setUser}
    onNavigate={onNavigate}

    openedVia={pageParams?.openedVia}
    returnTo={pageParams?.returnTo}
    arenaId={pageParams?.arenaId}
    arenaIndex={pageParams?.arenaIndex}
  />
)}

{page === 'holidayevent' && (
  <HolidayEvent
    user={user}
    setUser={setUser}
    onNavigate={onNavigate}
  />
)}

{page === 'thematicEvent' && (
  <ThematicEvent
    user={user}
    setUser={setUser}
    onNavigate={onNavigate}
  />
)}

{page === 'streakevent' && (
  <BattleStreakEvent
    user={user}
    setUser={setUser}
    onNavigate={onNavigate}
  />
)}

            {page === 'leaderboard' && (
              <Leaderboard
                user={user}
                setUser={setUser}
                onNavigate={onNavigate}
                openedVia={navSource}
                isGated={isGated}
                onGated={(action) => showGateModal(action || 'leaderboard')}
                initialView={pageParams?.initialView}
                tournamentEnabled={TOURNAMENT_MODAL_ENABLED}
                tournamentStartDate={TOURNAMENT_START_DATE}
                tournamentEndDate={TOURNAMENT_END_DATE}
                activeTournamentId={ACTIVE_TOURNAMENT_ID}
              />
            )}

            {page === 'tournaments' && (
              <Tournaments
                user={user}
                setUser={setUser}
                onNavigate={onNavigate}
                openedVia={navSource}
                isGated={isGated}
                onGated={(action) => showGateModal(action || 'leaderboard')}
                initialView={pageParams?.initialView}
                tournamentEnabled={TOURNAMENT_MODAL_ENABLED}
                tournamentStartDate={TOURNAMENT_START_DATE}
                tournamentEndDate={TOURNAMENT_END_DATE}
                activeTournamentId={ACTIVE_TOURNAMENT_ID}
              />
            )}

            {page === 'tournamentsV2' && (
  <TournamentsV2
    user={user}
    setUser={setUser}
    onNavigate={onNavigate}
    onClose={() => onNavigate('gamesHub')}
  />
)}

            {page === 'memoryGame' && (
  <MemoryGame
    user={user}
    setUser={setUser}
    onNavigate={guardedNavigate}
    onClose={() => guardedNavigate('arenas', 'memoryGame')}
    soundEnabled={soundEnabled}
    sfxEnabled={sfxEnabled}
    sfxVolume={sfxVolume}
  />
)}

            {page === 'tasks' && <TasksPage user={user} setUser={setUser} onNavigate={onNavigate}/>}

{page === 'challenges' && (
  <ChallengePage
    user={user}
    setUser={setUser}
    onNavigate={onNavigate}
    initialChallengeId={pageParams?.initialChallengeId}
    hasBattlePass={hasBattlePass}
    battlePassLoading={battlePassLoading}
  />
)}

            {page === 'friends' && <FriendsPage user={user} setUser={setUser} token={localStorage.getItem('token')} />}

{page === 'battlePass' && (
  <BattlePassPage
    user={user}
    token={localStorage.getItem('token')}
    setUser={setUser}
    onBattlePassStatusChange={setHasBattlePass}
    onNavigate={onNavigate}
    onOpenShop={(which) => {
      gate('shop', () => {
        setShopView(which);
        setPage('shop');
      });
    }}
  />
)}

            {page === 'gamepass' && (
              <GamepassPage token={localStorage.getItem('token')} onNavigate={onNavigate} user={user} setUser={setUser} onOpenShop={(which) => {gate('shop', () => {setShopView(which);setPage('shop');});}} />
            )}

            {page === 'battle' && <Battle {...pageParams} sfxEnabled={sfxEnabled} sfxVolume={sfxVolume} user={user} setUser={setUser} selectedCardId={setSelectedCardId} onUserUpdate={setUser} onNavigate={onNavigate} />}
            {page === 'marketplace' && (<Marketplace user={user} setUser={setUser} onNavigate={(p, params) => navigate(p, 'menu', params)} setSelectedCardId={setSelectedCardId} />)}
            {page === 'yearreview' && (
  <YearInReviewPage
    user={user} setUser={setUser} 
    onBack={() => navigate('home', 'YearReviewBack')}
  />
)}

          </main>

          {showWhitelistModal && (
  <div className="modal-backdrop">
    <motion.div
      className="modal fancy-success-modal"
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      style={{ textAlign: 'center' }}
    >
      <div style={{ fontSize: '72px', marginBottom: '10px' }}>🔒</div>
      <h2 style={{ color: '#ffd700', marginBottom: 8, textShadow: '1px 1px #000' }}>
        Whitelist Party in Progress 🎉
      </h2>
      <p style={{ color: '#ddd', textShadow: '1px 1px #000', lineHeight: 1.3, marginBottom: 10 }}>
          Early-bird chaos is live, so <b>{String(wlBlockedAction).toUpperCase()}</b> is taking a power nap.
          <br />
          Some areas are snoozing for now — we’ll wake them up soon.
          <br />
        </p>
        <div
          style={{
            display: 'none',
            padding: '10px 14px',
            borderRadius: 12,
            background: 'rgba(255,255,255,0.08)',
            fontWeight: 700,
            fontSize: 22,
            letterSpacing: 0.5,
            textShadow: '1px 1px #000',
            marginBottom: 4,
            marginTop: 10
          }}
        >
          {wlRemainingMs == null
            ? '…'
            : wlRemainingMs > 0
              ? formatDhms(wlRemainingMs)
              : 'Opening soon'}
        </div>
        
      <div className="modal-footer" style={{ marginTop: 12 }}>
        <button onClick={() => setShowWhitelistModal(false)}>Got it</button>
      </div>
    </motion.div>
  </div>
)}

          {showDailyModal && dailyData && (
 <DailyCheckInModal
  onClose={() => setShowDailyModal(false)}

  streak={dailyData.streak}

  awardedXp={dailyData.awardedXp ?? 0}
  bonusXp={dailyData.bonusXp ?? 0}

  rewardKind={dailyData.rewardKind}
  rewardAmount={dailyData.rewardAmount}

  claimedDays={
    Array.isArray(dailyData.claimedDays)
      ? dailyData.claimedDays
      : []
  }

  missedDays={
    Array.isArray(dailyData.missedDays)
      ? dailyData.missedDays
      : []
  }

  cycleMissed={
    dailyData.cycleMissed === true
  }

  allDaysClaimed={
    dailyData.allDaysClaimed === true
  }

  mainPrize={dailyData.mainPrize ?? null}

  mainPrizeGranted={
    dailyData.mainPrizeGranted === true
  }
/>
)}

          {showEarlyLockModal && (
  <div className="modal-backdrop">
    <motion.div
      className="modal fancy-success-modal"
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      style={{ textAlign: 'center' }}
    >
      <div style={{ fontSize: '72px', marginBottom: '10px' }}>🐦🔒</div>
      <h2 style={{ color: '#ffd700', marginBottom: 8, textShadow: '1px 1px #000' }}>
        Early Birds Only (for now)
      </h2>
      <p style={{ color: '#ddd', textShadow: '1px 1px #000', lineHeight: 1.3 }}>
        We’re gradually opening access. <b>{String(lockBlockedAction).toUpperCase()}</b> is currently
        available only to early birds. Please check back soon!
      </p>
      <div
          style={{
            display: 'inline-block',
            padding: '10px 14px',
            borderRadius: 12,
            background: 'rgba(255,255,255,0.08)',
            fontWeight: 700,
            fontSize: 22,
            letterSpacing: 0.5,
            textShadow: '1px 1px #000',
            marginBottom: 4,
            marginTop: 0
          }}
        >
          {wlRemainingMs == null
            ? '…'
            : wlRemainingMs > 0
              ? formatDhms(wlRemainingMs)
              : 'Opening soon'}
        </div>
      <div className="modal-footer" style={{ marginTop: 12 }}>
        <button onClick={() => setShowEarlyLockModal(false)}>Okay</button>
      </div>
    </motion.div>
  </div>
)}

          {showFeedbackModal && (
            <motion.div className="modal_box" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>
            <div className="modal_title">Feedback<button className="modal_close" onClick={() => setShowFeedbackModal(false)}/></div>
              <div className="modal_body">

                <div className="modal_inside">

                <img src="v2/menu_8.png" style={{height: 32}}/>
                <div className="shadow2" style={{fontWeight: 600, fontSize: 28}}>Send Feedback</div>
                
                <p
                className="shadow1"
                  style={{
                    fontWeight: 600,
                    marginTop: 15,
                    fontSize: '16px',
                  }}
                >
                  Tell us what you like, what’s confusing, or what we should improve.
                </p>

                <div className="info" style={{marginTop: 0}}>
                  <textarea
                    value={feedbackMessage}
                    onChange={(e) => {
                      setFeedbackMessage(e.target.value);
                      setFeedbackError('');
                      setFeedbackSuccess('');
                    }}
                    placeholder="Type your feedback here..."
                    className="text-input"
                    rows={4}
                    style={{
                      width: '100%',
                      height: 100,
                      padding: '12px 14px',
                      boxSizing: 'border-box',
                      borderRadius: 10,
                      border: '2px solid rgba(255,255,255,0.2)',
                      background: 'rgba(0,0,0,0.35)',
                      color: '#fff',
                      outline: 'none',
                      resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                  />

                  {feedbackError && (
                    <div
                      style={{
                        fontWeight: 'normal',
                        textShadow: '1px 1px #000',
                        color: '#ff6b6b',
                        marginTop: 8,
                        fontSize: 15,
                      }}
                    >
                      {feedbackError}
                    </div>
                  )}

                  {feedbackSuccess && (
                    <div
                      style={{
                        fontWeight: 'normal',
                        textShadow: '1px 1px #000',
                        color: '#7CFC00',
                        marginTop: 8,
                        fontSize: 15,
                      }}
                    >
                      {feedbackSuccess}
                    </div>
                  )}
                </div>
                </div>

                <div className="modal_footer">
                  <button
                  className="game-button green-button"
                    onClick={submitFeedback}
                    disabled={feedbackSending || feedbackMessage.trim().length < 3}
                    style={{ marginRight: 10,opacity: feedbackSending ? 0.7 : 1 }}
                  >
                    {feedbackSending ? 'Sending…' : 'Send Feedback'}
                  </button>
                  <button
                  className="game-button red-button"
                    onClick={() => setShowFeedbackModal(false)}
                    disabled={feedbackSending}
                  >
                    CLOSE
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          
{showGiftModal && gift && (
  <div className="modal-backdrop">
    <motion.div
      className="modal fancy-success-modal"
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      style={{ textAlign: 'center' }}
    >
      <div style={{ fontSize: 72, marginBottom: 10 }}>
  {hasAnyReward ? '🎁' : '❌'}
</div>

<h2 className="modal-title">
  {hasAnyReward ? 'You Received A Gift!' : 'Wrong Choice'}
</h2>

{hasAnyReward && giftMessage && (
  <div
    style={{
      marginTop: 10,
      marginBottom: 12,
      padding: '12px 14px',
      borderRadius: 12,
      background: 'rgba(0,0,0,0.35)',
      border: '1px solid rgba(255,255,255,0.16)',
      color: '#ddd',
      textShadow: '1px 1px #000',
      lineHeight: 1.35,
      fontSize: 18,
      whiteSpace: 'pre-wrap',
    }}
  >
    {giftMessage}
  </div>
)}

{hasAnyReward ? (
  <div
    style={{
      marginTop: 12,
      marginBottom: 16,
      textAlign: 'center',
      background: 'rgba(255,255,255,0.08)',
      borderRadius: 12,
      padding: '6px 50px',
      display: 'inline-block',
      fontSize: '21px',
    }}
  >
  {gift.spins > 0 && (
      <p>
        <img src="/fortuneicon.png" style={{ marginBottom: 5, height: 42 }} />
        <br />+{gift.spins} Spins
      </p>
    )}
    {gift.sparks > 0 && (
      <p>
        <img src="/sparks.png" style={{ marginBottom: 5, height: 42 }} />
        <br />+{gift.sparks} Sparks
      </p>
    )}
    {gift.runes > 0 && (
      <p>
        <img src="/runes.png" style={{ marginBottom: 5, height: 42 }} />
        <br />+{gift.runes} Runes
      </p>
    )}

    {toNum(gift?.tapi) > 0 && (
      <p>
        <img src="/icon_tapi.png" style={{ marginBottom: 5, height: 42 }} alt="TAPI" />
        <br />+{gift.tapi} TAPI
      </p>
    )}

    {gift.credits > 0 && <p>💰 +{gift.credits} Credits</p>}
    {gift.rarity && gift.rarity !== 'None' && (
      <p>
        <img src="/pack.png" style={{ marginBottom: 5, height: 64 }} />
        <br />{gift.rarity} Card
      </p>
    )}
    {hasGiftItems && (
      <p>
        +Special Item Added To Your Inventory
      </p>
    )}
  </div>
) : (
  <div
    style={{
      marginTop: 12,
      marginBottom: 16,
      padding: '14px 18px',
      borderRadius: 12,
      background: 'rgba(255,80,80,0.12)',
      border: '1px solid rgba(255,80,80,0.35)',
      color: '#ffb3b3',
      fontSize: 18,
      lineHeight: 1.35,
      textShadow: '1px 1px #000',
    }}
  >
    You picked the wrong option this time.
    <br />
    Better luck next time! 🍀
  </div>
)}

<div className="modal-footer">
        <button
          onClick={async () => {
            setShowGiftModal(false);

            // update local state
            setUser((prev) => ({
              ...prev,
              last_gift_seen: true,
            }));

            // persist to backend
            await fetch(`${process.env.REACT_APP_API_URL}/api/me/gift-seen`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
              },
            });
          }}
        >
          Got it
        </button>
      </div>

    </motion.div>
  </div>
)}


{showWarning && !showBlocked && (
  <div className="modal-backdrop">
    <motion.div
      className="modal fancy-success-modal"
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      style={{ textAlign: 'center', paddingRight: '12px', paddingLeft: '12px' }}
    >
      <div style={{ fontSize: 72, marginBottom: 10 }}>⚠️</div>

      <h2 className="modal-title" style={{ marginBottom: 8 }}>
        Warning
      </h2>

      <p style={{ fontSize: "21px", color: '#ddd', textShadow: '1px 1px #000', lineHeight: 1.35 }}>
        We detected suspicious activity on your account.
        <br />
        If this continues, your account may be blocked.
      </p>

      <div
        style={{
          marginTop: 12,
          padding: '10px 12px',
          borderRadius: 12,
          background: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.16)',
          fontSize: '16px',
          lineHeight: 1.35,
          textAlign: 'center',
          color: "#FFAF56"
        }}
      >
        Avoid multiple accounts, automation, or exploit attempts.
      </div>

      <div className="modal-footer" style={{ marginTop: 14 }}>
        <button onClick={() => setShowWarning(false)}>Got it</button>
      </div>
    </motion.div>
  </div>
)}

{showBlocked && (
<div
    style={{
      position: 'fixed',
      inset: 0,
      zIndex: 10000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 16,
      background: 'rgba(0,0,0,0.70)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
    }}
  >
    {/* animated background glow */}
    <div
      aria-hidden
      style={{
        position: 'absolute',
        inset: 0,
        background:
          'radial-gradient(circle at 20% 20%, rgba(255,0,80,0.18), transparent 40%),' +
          'radial-gradient(circle at 80% 10%, rgba(255,140,0,0.12), transparent 45%),' +
          'radial-gradient(circle at 50% 90%, rgba(255,0,0,0.14), transparent 40%)',
        filter: 'blur(20px)',
      }}
    />

    <div
      style={{
        position: 'relative',
        width: 'min(520px, 100%)',
        borderRadius: 18,
        padding: 18,
        background: 'rgba(20, 20, 24, 0.92)',
        border: '1px solid rgba(255,255,255,0.12)',
        boxShadow: '0 20px 60px rgba(0,0,0,0.55)',
        overflow: 'hidden',
      }}
    >
      {/* scanline */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: -40,
          height: 80,
          background:
            'linear-gradient(90deg, transparent, rgba(255,255,255,0.10), transparent)',
          transform: 'skewY(-8deg)',
          animation: 'blockScan 2.2s linear infinite',
        }}
      />

      {/* top accent */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: 18,
          padding: 1,
          background:
            'linear-gradient(135deg, rgba(255,60,120,0.75), rgba(255,140,0,0.35), rgba(255,0,0,0.55))',
          WebkitMask:
            'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
          pointerEvents: 'none',
          opacity: 0.85,
        }}
      />

      <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
        <div
          style={{
            width: 54,
            height: 54,
            borderRadius: 14,
            display: 'grid',
            placeItems: 'center',
            background:
              'radial-gradient(circle at 30% 30%, rgba(255,90,120,0.35), rgba(255,0,0,0.12))',
            border: '1px solid rgba(255,255,255,0.14)',
            boxShadow: '0 10px 30px rgba(255,0,80,0.18)',
            flex: '0 0 auto',
          }}
        >
          <span style={{ fontSize: 26 }}>⛔</span>
        </div>

        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 18,
              fontWeight: 800,
              letterSpacing: 0.2,
              color: 'rgba(255,255,255,0.96)',
            }}
          >
            Account Temporarily Disabled
          </div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.70)', marginTop: 2 }}>
            Your access has been disabled temporarily due to inactivity or rule violations.
          </div>
        </div>
      </div>

      <div
        style={{
          marginTop: 14,
          padding: 14,
          borderRadius: 14,
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.10)',
          color: 'rgba(255,255,255,0.82)',
          lineHeight: 1.35,
          fontSize: 18,
          textAlign: 'center'
        }}
      >
        Enter the code shown below to restore access to your account.
      </div>

      <div style={{ marginTop: 14, textAlign: 'center' }}>
  <img
    src={getUnbanImageUrl()}
    alt=""
    draggable={false}
    onError={() => setUnbanError('Could not load verification image.')}
    style={{
      width: '100%',
      maxWidth: 300,
      height: 100,
      objectFit: 'contain',
      borderRadius: 14,
      background: 'rgba(0,0,0,0.35)',
      border: '1px solid rgba(255,255,255,0.14)',
      userSelect: 'none',
    }}
  />

  <input
    value={unbanCode}
    onChange={(e) => {
      setUnbanCode(
        e.target.value
          .toUpperCase()
          .replace(/[^A-Z0-9]/g, '')
          .slice(0, 5)
      );
      setUnbanError('');
    }}
    onKeyDown={(e) => {
      if (e.key === 'Enter' && !unbanBusy) {
        submitUnbanCode();
      }
    }}
    placeholder="ENTER CODE"
    autoCapitalize="characters"
    autoComplete="off"
    spellCheck={false}
    style={{
      marginTop: 12,
      width: '100%',
      boxSizing: 'border-box',
      padding: '13px 14px',
      borderRadius: 14,
      border: '1px solid rgba(255,255,255,0.16)',
      background: 'rgba(0,0,0,0.32)',
      color: 'white',
      fontSize: 20,
      fontWeight: 900,
      textAlign: 'center',
      letterSpacing: 3,
      outline: 'none',
    }}
  />

  {unbanError && (
    <div
      style={{
        marginTop: 8,
        color: '#ff6b6b',
        fontSize: 14,
        fontWeight: 800,
        textShadow: '1px 1px #000',
      }}
    >
      {unbanError}
    </div>
  )}
</div>

      <div style={{ textAlign: 'center', width: '100%', marginTop: 12, fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>
        User ID: <span style={{ color: 'rgba(255,255,255,0.80)' }}>{user?.id}</span>
      </div>

           <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
        <button
          onClick={submitUnbanCode}
          disabled={unbanBusy}
          style={{
            flex: 1,
            padding: '12px 14px',
            borderRadius: 14,
            border: '1px solid rgba(255,255,255,0.14)',
            background: unbanBusy
              ? 'rgba(255,255,255,0.12)'
              : 'linear-gradient(135deg, rgba(46,204,113,0.95), rgba(39,174,96,0.85))',
            color: 'white',
            fontWeight: 800,
            cursor: unbanBusy ? 'not-allowed' : 'pointer',
          }}
        >
          {unbanBusy ? 'Checking...' : 'Unban Me'}
        </button>

        <button
          onClick={reloadUnbanImage}
          disabled={unbanBusy}
          style={{
            padding: '12px 14px',
            borderRadius: 14,
            border: '1px solid rgba(255,255,255,0.14)',
            background: 'rgba(255,255,255,0.06)',
            color: 'rgba(255,255,255,0.95)',
            fontWeight: 800,
            cursor: unbanBusy ? 'not-allowed' : 'pointer',
            minWidth: 110,
          }}
        >
          Reload
        </button>
      </div>
    </div>

    <style>{`
      @keyframes blockScan {
        0%   { transform: translateY(0) skewY(-8deg); opacity: 0.0; }
        10%  { opacity: 0.6; }
        60%  { opacity: 0.35; }
        100% { transform: translateY(520px) skewY(-8deg); opacity: 0.0; }
      }
    `}</style>
  </div>
)}

{showCmsModal && cmsModal && (
  <div
    className="modal_box"
    onClick={() => { setShowCmsModal(false); setCmsModal(null); }}
  >
  <div className="modal_title">
{cmsModal.title} <button className="modal_close"  onClick={() => {
  if (cmsModal?.id) {
    cmsDismissedThisBootRef.current.add(String(cmsModal.id));
  }

  setShowCmsModal(false);
  setCmsModal(null);
}}/>
  </div>
    <motion.div
      className="modal_body"
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      onClick={(e) => e.stopPropagation()} // prevent backdrop click from closing when clicking inside
    >
    <div className="modal_inside">
      {/* Optional icon */}
      {cmsModal.icon ? (
  isImageIcon(cmsModal.icon) ? (
    <img
      src={String(cmsModal.icon).trim()}
      alt=""
      style={{
        width: 'auto',
        height: 64,
        objectFit: 'cover',
        marginBottom: 10,
        flex: '0 0 auto',
      }}
      onError={(e) => { e.currentTarget.style.display = 'none'; }}
    />
  ) : (
    <div style={{ fontSize: 52, marginBottom: 20, lineHeight: 1 }}>
      {cmsModal.icon}
    </div>
  )
) : null}


      {/* Subtitle */}
      {cmsModal.subtitle ? (
        <p style={{ color: '#ddd', textShadow: '1px 1px #000', lineHeight: 1.35, marginBottom: 10 }}>
          {renderRichText(cmsModal.subtitle)}
        </p>
      ) : null}

      {/* Content image */}
{cmsModal.contentImage ? (
  <img
    src={String(cmsModal.contentImage).trim()}
    alt=""
    style={{
      display: 'block',
      width: '100%',
      height: 'auto',
      borderRadius: 8,
    }}
  />
) : null}

      {cmsModal.content ? (
        <div
          style={{
            marginBottom: 12,
            padding: '14px 14px',
            borderRadius: 12,
            background: 'rgba(0,0,0,0.35)',
            border: '1px solid rgba(255,255,255,0.16)',
            color: '#ddd',
            textShadow: '1px 1px #000',
            lineHeight: 1.35,
            fontSize: 20,
            textAlign: 'left',
            whiteSpace: 'pre-wrap',
          }}
        >
           {renderRichText(cmsModal.content)}
        </div>
      ) : null}

      {/* Footer text */}
      {cmsModal.footer ? (
        <div style={{ marginBottom: 12, opacity: 0.85, fontSize: 16 }}>
          {renderRichText(cmsModal.footer)}
        </div>
      ) : null}

</div>
      <div className="modal_footer" style={{ marginTop: 0 }}>
  {(
  cmsModal.button?.text &&
  (
    cmsModal.button?.to ||
    cmsModal.button?.url
  )
) ? (
    <button className="game-button green-button"
      onClick={() => {
  if (cmsModal?.id) {
    cmsDismissedThisBootRef.current.add(
      String(cmsModal.id)
    );
  }

  const to =
    String(
      cmsModal.button?.to || ''
    ).trim();

  const url =
    String(
      cmsModal.button?.url || ''
    ).trim();

  setShowCmsModal(false);
  setCmsModal(null);

  if (url) {
    const tg =
      window.Telegram?.WebApp;

    if (
      tg &&
      typeof tg.openLink === 'function'
    ) {
      tg.openLink(url);
    } else {
      window.open(
        url,
        '_blank',
        'noopener,noreferrer'
      );
    }

    return;
  }

  if (to) {
    guardedNavigate(
      to,
      'cms-modal'
    );
  }
}}
    >
      {cmsModal.button.text}
    </button>
  ) : null}

  <button
  className="game-button red-button"
  style={{ marginLeft: 10 }}
  onClick={() => {
    if (cmsModal?.id) {
      cmsDismissedThisBootRef.current.add(
        String(cmsModal.id)
      );
    }

    setShowCmsModal(false);
    setCmsModal(null);
  }}
>
  CLOSE
</button>
</div>

    </motion.div>
  </div>
)}


{showGlobalSideModal && (
  <div
    className="side-modal-backdrop"
    style={{
      position: 'fixed',
      inset: 0,
      zIndex: 9999,
      pointerEvents: 'auto',
      backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${sides[selectedSideIndex].image})`,
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
    }}
    onClick={(e) => e.stopPropagation()}
  >

    <img src="/v2/splash_top.png" style={{borderBottom: "3px solid #6a2306",display: "block", position: "fixed", top: 0, left: 0, width: "100%", zIndex: 1}}/>
    <img src="/v2/splash_bottom.png" style={{display: "block", position: "fixed", bottom: 0, left: 0, width: "100%"}}/>

    <motion.div
      className="side-modal-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <h2 className="side-modal-title" style={{fontSize: "24px"}}>Choose Your World</h2>

      <h3 className="side-modal-title-2" style={{color: "#FFF04E", marginTop: -10, fontSize: "38px",marginBottom: "30px"}}>
        {sides[selectedSideIndex].emoji} {sides[selectedSideIndex].title}
      </h3>

      <div className="side-modal-slider">
        <button
          className="side-modal-arrow left"
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedSideIndex((prev) => (prev === 0 ? 1 : prev - 1));
          }}
        >
          <img src="/v2/left.png" alt="Previous" className="side-arrow-icon" />
        </button>

        <div className="side-modal-slide">
          <img
            style={{ height: '300px' }}
            src={sides[selectedSideIndex].tapimage}
            alt={sides[selectedSideIndex].title}
          />
          <p style={{fontWeight: 600}}>{sides[selectedSideIndex].description}</p>
        </div>

        <button
          className="side-modal-arrow right"
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedSideIndex((prev) => (prev === 1 ? 0 : prev + 1));
          }}
        >
          <img src="/v2/right.png" alt="Next" className="side-arrow-icon" />
        </button>
      </div>

      {sideErrorMessage && (
        <div style={{ color: '#ff6b6b', marginTop: 10, fontWeight: 700 }}>
          {sideErrorMessage}
        </div>
      )}

      <div className="side-modal-footer">
        <button
          type="button"
          disabled={isChoosingSide}
          onClick={(e) => {
            e.stopPropagation();
            handleSelectSideGlobal(sides[selectedSideIndex].key); // key is 'good'/'bad'
          }}
          className="side-modal-choose-btn image-button"
          style={{
            pointerEvents: 'auto',
            opacity: isChoosingSide ? 0.7 : 1,
            cursor: isChoosingSide ? 'wait' : 'pointer',
          }}
        >
          <img
            src="/v2/select.png"
            alt={`Choose ${sides[selectedSideIndex].title}`}
            className="side-select-image"
            style={{ pointerEvents: 'none' }}
          />
        </button>
      </div>
    </motion.div>
  </div>
)}

          {showWalletModal && (
            <div className="modal-backdrop">
              <motion.div
                className="modal fancy-success-modal"
                style={{ paddingRight: '12px', paddingLeft: '12px' }}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <h2 className="modal-title">🔐 Set Your TON Wallet</h2>
                <p style={{ fontWeight: 'normal', textShadow: '1px 1px #000', opacity: 0.85, marginTop: 4, fontSize: '18px' }}>
                  Enter the TON wallet where you want to receive rewards:
                </p>

                <div style={{ float: 'left', width: '100%', marginBottom: 18, marginTop: 0 }}>
                  <input
                    type="text"
                    value={walletInput}
                    onChange={(e) => setWalletInput(e.target.value)}
                    placeholder="YourWalletAddress"
                    className="text-input"
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      boxSizing: 'border-box',
                      borderRadius: 10,
                      border: '2px solid rgba(255,255,255,0.2)',
                      background: 'rgba(0,0,0,0.35)',
                      color: '#fff',
                      outline: 'none',
                    }}
                  />
                  {walletError && (
                    <div style={{ fontWeight: 'normal', textShadow: '1px 1px #000', color: '#ff6b6b', marginTop: 8, fontSize: 15 }}>
                      {walletError}
                    </div>
                  )}
                </div>

                <div className="modal-footer" style={{ marginTop: 16 }}>
                  <button onClick={() => setShowWalletModal(false)} disabled={walletSaving}>
                    Cancel
                  </button>
                  <button onClick={saveWallet} disabled={walletSaving} style={{ opacity: walletSaving ? 0.7 : 1 }}>
                    {walletSaving ? 'Saving…' : 'Save Wallet'}
                  </button>
                </div>
              </motion.div>
            </div>
          )}

          
  <Footer
    whitelistEnabled={isGated()}
    onWhitelistBlocked={(page) => showGateModal(page)}
    currentPage={page}
    navSource={navSource}
    onNavigate={(p, params) => guardedNavigate(p, 'Footer', params)}
    hasLiveGame={hasLiveGame}
    hasTaskReward={hasTaskReward}
  />

        </div>
      )}

{showTournamentsModal && (
  <TournamentModal
    onNavigate={onNavigate}
    onClose={() => {
      localStorage.setItem(tournamentKey, "1");
      setShowTournamentsModal(false);
    }}
  />
)}

{showTournamentTwiceDailyModal && (
  <div className="modal-backdrop">
    <motion.div
      className="modal fancy-success-modal"
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      style={{ maxWidth: 300, padding: 10, paddingTop: 16, paddingBottom: 16, textAlign: 'center' }}
    >
      <h1 className="pagetitle" style={{color: '#FDDE66'}}>Tournament Rewards</h1>
      <img
        src="/tournamentmodal.png"
        alt="Tournament"
        style={{
          width: '100%',
          cursor: 'pointer',
        }}
        onClick={() => {
          setShowTournamentTwiceDailyModal(false);
          guardedNavigate('tournaments', 'tournament-twice-daily-modal');
        }}
      />

      <div style={{marginTop: 10, fontSize: 18, textShadow: "1px 1px #000"}}>Don’t waste any time — join the tournament now!</div>

      <div
  className="buy"
  onClick={() => {
    setShowTournamentTwiceDailyModal(false);
    onNavigate("tournaments");
  }}
  style={{
    width: 160,
    height: 42,
    lineHeight: '42px',
    marginTop: 15,
    fontSize: 21,
    textShadow: '1px 1px #000',
    display: 'inline-block',
    backgroundImage: "url('/buy.png')",
    backgroundSize: "100% 42px",
    backgroundRepeat: "repeat",
    backgroundPosition: "center bottom",
    cursor: "pointer",
  }}
>
  ENTER TOURNAMENT
</div>

      <div className="modal-footer" style={{ marginTop: 5 }}>
        <button onClick={() => setShowTournamentTwiceDailyModal(false)}>
          Not Today
        </button>
      </div>
    </motion.div>
  </div>
)}

{showHighTrophiesModal && (
  <motion.div
    className="modal_box"
    initial={{ scale: 0.8, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
  >
    <div className="modal_title">
      NEW CHEST AVAILABLE

      <button
        className="modal_close"
        onClick={() => setShowHighTrophiesModal(false)}
      ></button>
    </div>

    <div className="modal_body">
      <div className="modal_inside" style={{ padding: 5 }}>
        <img
          src="/frostmodal.gif"
          alt=""
          style={{
            borderRadius: 10,
            width: "100%"
          }}
        />
      </div>

      <div className="modal_footer">
        <button
          className="game-button green-button"
          style={{ marginRight: 10 }}
          onClick={() => {
            setShowHighTrophiesModal(false);
            guardedNavigate("shop", "high-trophies-chest", {
              initialView: "items"
            });
          }}
        >
          BUY NOW
        </button>

        <button
          className="game-button red-button"
          onClick={() => setShowHighTrophiesModal(false)}
        >
          CLOSE
        </button>
      </div>
    </div>
  </motion.div>
)}

{showTwiceDailyModal && twiceDailyModalData && (
  <motion.div className="modal_box" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
    <div className="modal_title">
      Welcome Offer
 <button className="modal_close" onClick={() => setShowTwiceDailyModal(false)}></button>
      </div>
      <div className="modal_body">
        
        <div className="modal_inside" style={{padding: 0,position: "relative",display: "flex",gap: 10,alignItems: "stretch",}}>
        <img src="/ogpassmodal.png" style={{aspectRatio: 1,width: "100%"}}/>
       <img src="/pass2.png" style={{height: 120,position: "absolute", bottom: 20, right: 20}}/>

      </div>

        <div className="modal_footer">

          <div className="modal_counter">Valid For:<div className="modal_clock"><OfferCountdown targetIso={WELCOME_OFFER_END_AT} /></div></div>

          <button className="game-button yellow-button" style={{position: "relative", fontSize: 24}} onClick={() => {
  setShowTwiceDailyModal(false);
  guardedNavigate('battlePass', 'twice-daily-modal');
}}>
            Enter <strike className="shadow1" style={{fontSize: 21,fontWeight: 500,marginLeft: 8,marginRight: 8,color: "#ffe686"}}>(1000)</strike> 750 <img src="/v2/tstar.png" style={{marginLeft: 0,marginTop: -1,height: 36,verticalAlign: "middle"}}/>
            <img src="/v2/badge.png" style={{marginLeft: 5,marginRight: -20}}/>
            <span style={{color: "#fff", fontSize: 18,position: "absolute",right: 3}}>25%</span>
          </button>
        </div>
      </div>
    
  </motion.div>
)}

{showSeasonChangeModal && (
  <SeasonChangeModal
    user={user}
    setUser={setUser}
    mode={seasonChangeMode}
    onNavigate={onNavigate}
    onClose={() => {
      localStorage.setItem(`seasonChangeModal:${seasonChangeMode}`, "1");
      setShowSeasonChangeModal(false);
    }}
  />
)}

      <SettingsModal
  open={showSettings}
  onClose={() => setShowSettings(false)}
  bgmAudio={bgmRef.current}
  sfxAudio={null} // if SFX lives in TapGame, don’t pass audio element; just pass state
  musicEnabled={soundEnabled}
  setMusicEnabled={(v) => {
  setAudioUnlocked(true);
  setSoundEnabled(v);
  try { localStorage.setItem(SOUND_KEY, v ? '1' : '0'); } catch {}
}}
  sfxEnabled={sfxEnabled}
  setSfxEnabled={setSfxEnabled}
  musicVolume={musicVolume}
  setMusicVolume={setMusicVolume}
  sfxVolume={sfxVolume}
  setSfxVolume={setSfxVolume}
  onHelp={() => window.open('https://t.me/taptopia_io', '_blank')}
  onPrivacy={() => window.open('/privacy', '_blank')}
  onTerms={() => window.open('/terms', '_blank')}
/>

    </div>



  );
}

export default App;
